/**
 * Vietnamese translation bundle (default locale).
 *
 * Source of truth — every English / Chinese key MUST exist with the same
 * shape per the i18n parity invariant in MP9 §3.2. The structure mirrors
 * the screen taxonomy from `iSale Localized EN-ZH.html` D1 glossary.
 */

const vi = {
  common: {
    // GA-M-05/GA-W-07 hardcode sweep (2026-06-05) — moved out of TSX
    todayShort: "Hôm nay",
    processing: "Đang xử lý",
    daysShort: "{{d}} ngày",
    weeksShort: "{{n}} tuần",
    monthsShort: "{{n}} tháng",
    backToDashboard: "← Về dashboard",
    a11yClose: "Đóng",
    a11yCloseDialog: "Đóng dialog",
    a11yOpenGuide: "Mở hướng dẫn 30 giây",
    a11yNewUpdates: "Có cập nhật mới",
    a11yNewItems: "{{count}} mục mới",
    appName: "iSale",
    save: "Lưu",
    cancel: "Huỷ",
    close: "Đóng",
    clear: "Xoá",
    delete: "Xoá",
    confirm: "Xác nhận",
    continue: "Tiếp tục",
    back: "Quay lại",
    /** Template for back-link "Quay lại {parent}" used by Layout depth-2. */
    backTo: "Quay lại {{parent}}",
    next: "Tiếp →",
    loading: "Đang tải…",
    search: "Tìm kiếm…",
    saving: "Đang lưu…",
    creating: "Đang tạo…",
    error: "Có lỗi xảy ra",
    retry: "Thử lại",
    skip: "Bỏ qua",
    done: "Xong",
    yes: "Có",
    no: "Không",
    seeAll: "Tất cả →",
    // Standardised toast templates — audit P1-07 (2026-05-26). Use these
    // instead of inline `Không lưu được · ${err}` strings sprinkled across
    // screens so error UX stays consistent across vi/en/zh.
    saveFailed: "Không lưu được · {{error}}",
    deleteFailed: "Không xoá được · {{error}}",
    createFailed: "Không tạo được {{what}} · {{error}}",
    loadFailed: "Không tải được · {{error}}",
    createClipFailed: "Không tạo được clip · {{error}}",
    createCheckoutFailed: "Không tạo được checkout · {{error}}",
    deleteAccountFailed: "Không xoá được tài khoản · {{error}}",
    // EP-8 offline banner
    offlineBanner: "Không có mạng — đang hiện dữ liệu đã lưu",
    offlineBannerNoCache: "Không có mạng — kết nối lại để tải dữ liệu",
  },
  splash: {
    /** Tagline below the iSale wordmark on the boot splash (Hi-Fi 01). */
    tagline: "Clip bán hàng AI · trong 3 phút",
  },
  signIn: {
    heroLine: "Mở app,\n3 phút có clip.",
    // Web v2 (2026-05-25 P1.1 close): each line split so JSX renders
    // line break without depending on \n-from-i18n parsing edge cases.
    heroLine1: "Mở app,",
    heroLine2: "3 phút có clip.",
    subtitle:
      "Đăng nhập để tạo clip bán hàng đầu tiên — đăng được TikTok, Reels, Shorts & Shopee Video, không cần biết edit.",
    /** Restored full sub-copy per Hi-Fi spec (cross-platform messaging). */
    subCopy:
      "Đăng nhập để tạo clip bán hàng đầu tiên — đăng được TikTok, Reels, Shorts & Shopee Video, không cần biết edit. Đồng bộ với ứng dụng iSale trên iPhone & Android.",
    tagline: "Clip bán hàng AI · web",
    googleButton: "Tiếp tục với Google",
    appleButton: "Tiếp tục với Apple",
    continueGoogle: "Tiếp tục với Google",
    continueApple: "Tiếp tục với Apple",
    continueEmail: "Đăng nhập bằng email",
    or: "hoặc",
    redirecting: "Đang chuyển hướng…",
    appleNotConfigured: "Đăng nhập Apple sắp có. Tạm dùng Google nhé.",
    emailComingSoon: "Đăng nhập email sắp có ở Phase 2. Tạm dùng Google hoặc Apple.",
    /** FL-6: shown when APPLE_CLIENT_ID is not configured (Apple button hidden). */
    emailComingSoonGoogleOnly: "Đăng nhập email sắp có ở Phase 2. Tạm dùng Google nhé.",
    trustSpeed: "~3 phút / clip",
    trustNative: "Made for Vietnam",
    legalPrefix: "Bằng cách đăng nhập, bạn đồng ý với",
    legalTerms: "Điều khoản",
    legalPrivacy: "Chính sách riêng tư",
    legalAnd: "và",
    terms: "Điều khoản",
    privacy: "Chính sách riêng tư",
    captionHook: "Da khô như sa mạc?",
  },
  home: {
    greetingMorning: "Chào buổi sáng",
    greetingAfternoon: "Chào buổi chiều",
    greetingEvening: "Chào buổi tối",
    greetingNight: "Vẫn thức khuya à",
    quotaLabel: "THÁNG NÀY",
    quotaUsageSuffix: "video đã dùng",
    upgradeCta: "Nâng cấp →",
    recentProducts: "Sản phẩm gần đây",
    recentClips: "Clip vừa tạo",
    fabLabel: "Tạo clip mới",
    statusReady: "Chờ render",
    statusDraft: "Nháp",
    statusArchived: "Lưu trữ",
    statusRendered: "Hoàn tất",
    greetingAvatarA11y: "Mở Tài khoản",
    greetingBellA11y: "Mở Thông báo",
    emptyClipsHint: "Chưa có clip nào — tap \"{{cta}}\" để bắt đầu",
  },
  settings: {
    // GA-X-01 Option B (2026-06-05): toggle → real status row
    aiConsentAccepted: "Đã đồng ý · {{date}}",
    aiConsentNeedsReconsent: "Cần đồng ý lại điều khoản mới",
    aiConsentNotAccepted: "Chưa đồng ý",
    title: "Cài đặt",
    sectionPermissions: "Quyền & dữ liệu",
    sectionNotifications: "Thông báo",
    sectionOther: "Khác",
    sectionAccount: "Tài khoản",
    signOutNote: "Thoát phiên đăng nhập trên thiết bị này",
    aiConsent: "Đồng ý dùng AI",
    aiConsentNote: "3 provider · từ 5/2026",
    auditLog: "Nhật ký AI",
    auditLogNote: "Xem chi tiết mọi cuộc gọi",
    requestDelete: "Yêu cầu xoá dữ liệu",
    requestDeleteNote: "GDPR · ATTP",
    notifyRender: "Khi clip render xong",
    notifyTips: "Mẹo bán hàng bằng video",
    notifyPromo: "Khuyến mãi gói cước",
    language: "Ngôn ngữ",
    appearance: "Giao diện",
    themeLight: "Sáng",
    themeDark: "Tối",
    themeSystem: "Tự động",
    signOut: "Đăng xuất",
    accountGuest: "Khách",
    accountUserFallback: "Bạn 👋",
    accountWorkspaceFallback: "Không gian làm việc cá nhân",
    accountPlanFree: "Gói Free",
    requestDeleteNoteLong: "GDPR · ATTP · 30 ngày soft-delete",
    /** P3.4 — Strategy Anchor 4: trust through transparency. */
    complianceDict: "Quy tắc kiểm duyệt nội dung",
    complianceDictNote: "Xem danh sách từ khoá AI sẽ flag · cập nhật hàng tuần",
    aiConsentNoteShort: "3 provider · từ 5/2026",
    auditLogNoteShort: "Xem chi tiết mọi cuộc gọi",
    // B7 from iSale-User-Flow-Index.md §6 — mobile Settings was missing
    // the Billing entry point. Row uses these labels.
    billingMenuTitle: "Gói cước",
    billingMenuNote: "Xem quota · nâng cấp Pro",
    // Web Settings merge 2026-05-22 PM: replace inline 3-toggle notif
    // section with single LinkRow → /settings/notifications (7-toggle V026 page).
    notificationsMenuTitle: "Thông báo",
    notificationsMenuNote: "Render, quota, khuyến mãi",
    footerBuild: "iSale v0.1.0 · build {{date}}",
  },
  billing: {
    title: "Gói cước",
    currentPlan: "ĐANG DÙNG",
    videosThisMonth: "video tháng này",
    resetOn: "Reset",
    starterName: "Starter",
    /** B6 (marketing audit 2026-05-25) — translate abstract volume into
     *  cadence matching Strategy §4.1 Tier-1 target ("5-10 video/tuần"). */
    starterTagline: "Cho shop mới mở · 30 clip đủ test thị trường",
    proName: "Pro",
    proTagline: "Cho shop đẩy mạnh · 5 clip/ngày, giữ nhịp đăng đều",
    enterprise: "Enterprise",
    contactSales: "Liên hệ",
    chooseStarter: "Chọn Starter",
    choosePro: "Chọn Pro",
    using: "Đang dùng",
    freeName: "Free",
    freeTagline: "Đủ để thử + đăng vài clip nhỏ",
    lifetimeName: "Lifetime",
    lifetimeTagline: "Trả 1 lần, dùng mãi — giới hạn 200 người đầu tiên",
    registerEarly: "Đăng ký sớm",
    popular: "PHỔ BIẾN NHẤT",
    footer:
      "Huỷ bất cứ lúc nào · Hoàn tiền 7 ngày · Thanh toán bằng Momo, ZaloPay, Visa",
    // MP11 §3.3 — Pro Annual upsell (Hi-Fi 10 mobile + web)
    proAnnualTitle: "Pro · Năm",
    proAnnualPrice: "{{price}}/năm",
    periodSavingsBadge: "tiết kiệm 2 tháng",
    proAnnualSavings: "Tiết kiệm {{saved}}/năm so với trả tháng",
    proAnnualSaveBadge: "Tiết kiệm 20%",
    proAnnualSubtitle: "Tương đương {{monthly}}/tháng",
    periodSelectMonth: "Tháng",
    /** Bare "Năm" label — savings now communicated by the dedicated
     *  periodSavingsBadge pill rendered next to the label (P3.3, 2026-05-25).
     *  The old "· -17%" was a Hi-Fi placeholder before annual pricing was
     *  finalized; current Pro Annual ($115/yr vs $12/mo × 12 = $144) saves
     *  $29/yr ≈ 20% (D-022 canonical). */
    periodSelectYear: "Năm",
  },
  facts: {
    title: "Fact Locker",
    headerHook: "Verify trước, generate sau",
    heroTitle: "Khẳng định facts AI tìm ra",
    introBody:
      "Mỗi fact dưới đây cần bạn verify trước khi iSale gửi sang LLM viết kịch bản. Đây là hàng rào chống AI bịa — bạn là người chịu trách nhiệm cuối cùng cho mỗi claim.",
    verifiedLabel: "Đã xác nhận",
    verifiedNeeded: "cần thiết",
    riskyWarningTitle: "fact đang flag compliance",
    riskyWarningBody:
      "Các fact có nhãn ⚠️ chứa từ ngữ vi phạm quy tắc VN (mỹ phẩm, TPCN, dược…). Sửa hoặc xác nhận có chứng cứ trước khi verify.",
    fieldSellingPoint: "Điểm bán nổi bật",
    fieldTargetAudience: "Đối tượng",
    fieldIngredient: "Thành phần",
    fieldClaim: "Lời khẳng định",
    fieldCategoryInference: "Danh mục AI đoán",
    ctaCreateScript: "Tạo clip →",
    ctaCreateScriptNeeded: "Cần verify {{n}} fact nữa",
    createScriptFailed: "Không tạo được script · thử lại",
    productNotReady: "Sản phẩm chưa sẵn sàng — chờ phân tích AI xong rồi thử lại",
    addFieldCta: "Thêm",
    emptyFieldHint: "— chưa có fact —",
  },
  clipFlow: {
    entryCta: "Tạo clip",
    resumeTitle: "Tiếp tục clip đang làm",
    resumeCta: "Tiếp tục →",
    resumeChip: "↻ Đang làm dở",
    step1: "Sản phẩm",
    step2: "Kịch bản",
    step3: "Chỉnh",
    step4: "Xuất",
    stepLabel: "Bước {{n}}/4",
    navNext: "Tiếp tục →",
    navBack: "← Quay lại",
    navSaveExit: "Lưu nháp & thoát",
    exitConfirmTitle: "Thoát tạo clip?",
    exitConfirmBody: "Về Trang chủ? Bạn có thể tiếp tục clip đang làm dở sau.",
    exitConfirmLeave: "Thoát",
    factGateTitle: "Xác nhận để AI viết đúng",
    factGateBody: "AI chỉ viết từ thông tin bạn đã xác nhận — không bịa thêm.",
    factGateProgress: "{{done}}/{{total}} đã xác nhận",
    factGateDone: "Đã xác nhận đủ ✓",
    factGateRegulated: "⚠ cần giấy tờ",
    captionLabel: "Caption đăng bài",
    captionRegenerate: "↻ Tạo lại caption",
    exportReady: "Clip đã sẵn sàng 🎉",
    exportPlatforms: "Đăng lên nền tảng",
    finishCta: "Hoàn tất · Tạo clip khác →",
    entryNoProduct: "Hãy chọn một sản phẩm để bắt đầu tạo clip.",
    entryProductError:
      "Không mở được sản phẩm này — có thể đã bị xoá hoặc bạn không có quyền.",
    entryMissing: "Thiếu thông tin cho bước này. Hãy bắt đầu lại từ sản phẩm.",
    entryPickProduct: "Chọn sản phẩm →",
    advancedTitle: "Tuỳ chỉnh nâng cao",
    advancedHint: "Chọn định dạng kịch bản — mặc định Cảm nhận",
    advancedOpen: "Mở ▾",
    advancedClose: "Thu gọn ▴",
  },
  clipFormats: {
    review: "Cảm nhận",
    unboxing: "Mở hộp",
    beforeAfter: "Trước–Sau",
    demo: "Hướng dẫn dùng",
    story: "Câu chuyện",
    promotion: "Khuyến mãi",
    testimonial: "Khách hàng nói",
    faq: "Hỏi–Đáp",
  },
  clipTemplates: {
    // GA-M-05/GA-W-07 hardcode sweep (2026-06-05)
    unknownType: "Loại clip không tồn tại",
    detailNextStepHint: "Bước tiếp: nhập tên sản phẩm + tải ảnh",
    // Section + route titles
    sectionTitle: "Tạo clip từ mẫu",
    sectionSubtitle: "iSale viết kịch bản đúng cấu trúc + giọng văn hợp tệp khách",
    sectionBadge: "★ Mới",
    libraryTitle: "Chọn loại clip bạn muốn tạo",
    libraryIntro: "Mỗi loại có cấu trúc kịch bản riêng — chọn 1 trong 4 loại bên dưới.",
    detailBreadcrumbLibrary: "Mẫu clip",
    // CTAs
    ctaSeeAll: "Xem tất cả",
    ctaUseTemplate: "Dùng mẫu này →",
    ctaUseTemplateRegulated: "Tôi đã hiểu — Dùng mẫu này",
    ctaCardSeeDetail: "Xem chi tiết →",
    // Clip types
    unboxing: {
      name: "Mở hộp",
      shortDesc: "Khoe hàng thật · tạo lòng tin",
      useWhen: "Hàng mới về, cần tạo lòng tin, bao bì đẹp",
      step1: "Hook mở hộp gây tò mò",
      step2: "Khoe sản phẩm & chi tiết",
      step3: "Điểm nổi bật khi cầm thật",
      step4: "Cảm nhận đầu tiên",
      step5: "CTA nhẹ",
    },
    flashSale: {
      name: "Flash sale",
      shortDesc: "Đẩy mua nhanh · ưu đãi gấp",
      useWhen: "Đợt sale, xả kho, deal giới hạn",
      step1: "Hook giá sốc",
      step2: "Sản phẩm + ưu đãi cụ thể",
      step3: "Lý do mua nhanh (urgency)",
      step4: "CTA mạnh — Đặt ngay",
    },
    review: {
      name: "Cảm nhận",
      shortDesc: "Đánh giá thực · trải nghiệm",
      useWhen: "Cần thuyết phục bằng trải nghiệm, nhiều điểm bán",
      step1: "Hook nêu vấn đề khách gặp",
      step2: "Sản phẩm là giải pháp",
      step3: "2–3 ưu điểm chính",
      step4: "Trải nghiệm thật",
      step5: "CTA",
    },
    beforeAfter: {
      name: "Trước–Sau",
      shortDesc: "So sánh kết quả · mỹ phẩm",
      useWhen: "Sản phẩm có kết quả nhìn thấy được (mỹ phẩm, TPCN)",
      step1: "Tình trạng \"trước\"",
      step2: "Giới thiệu sản phẩm",
      step3: "Quá trình dùng",
      step4: "Kết quả \"sau\"",
      step5: "CTA",
    },
    // Audience
    audience: {
      pickerLabel: "Đối tượng khách hàng",
      pickerHint: "(chọn 1)",
      general: { name: "Chung", tone: "Trung tính, dễ tiếp cận" },
      genZ: { name: "Gen Z (16–24)", tone: "Trẻ trung, bắt trend, ngôn ngữ casual" },
      office: { name: "Văn phòng (25–35)", tone: "Bận rộn — nhấn tiện lợi + chất lượng" },
      mom: { name: "Mẹ bỉm sữa", tone: "Nhấn an toàn, cho con / gia đình" },
      male: { name: "Khách nam", tone: "Đi thẳng vấn đề, ít hoa mỹ" },
      mature: { name: "Trung niên (40+)", tone: "Rõ ràng, tin cậy, không slang" },
    },
    // Compliance
    complianceChip: "⚠ kiểm tra QC",
    complianceCardTitle: "Lưu ý quảng cáo mỹ phẩm / TPCN",
    complianceCardBody: "Clip kiểu này dễ chạm Nghị định 181/2013/NĐ-CP. iSale sẽ kiểm tra script trước khi render — nếu có claim hiệu quả rủi ro, em sẽ gợi ý chỉnh hoặc yêu cầu giấy chứng nhận.",
    complianceSeeRules: "Xem quy định đầy đủ",
    // Meta labels
    metaDuration: "Độ dài gợi ý",
    metaRender: "Render template",
    metaSubtitle: "Phụ đề",
    scriptStructureTitle: "Cấu trúc kịch bản",
    descriptionTitle: "Mẫu này làm gì",
    // Empty / error
    emptyTitle: "Chưa tải được mẫu clip",
    emptyBody: "Có vẻ kết nối mạng đang chậm. Em sẽ thử lại sau ít giây — hoặc bấm bên dưới.",
    emptyRetry: "Thử lại",
    emptyFallback: "Tạo clip nhanh không qua mẫu",
    // Chip ngữ cảnh (InfoStep)
    contextChip: "Mẫu: {{type}} · {{audience}}",
    contextChipRemove: "Bỏ mẫu",
  },
  factsMobile: {
    // GA-M-05 a11y labels (2026-06-05)
    a11yVerify: "Verify",
    a11yUnverify: "Bỏ verify",
    a11yEdit: "Sửa",
    a11yDelete: "Xoá",
    pullToRefresh: "Kéo xuống để tải lại",
    loadingLabel: "Đang tải facts...",
    sourceVision: "AI nhận diện",
    sourceUserInput: "Bạn thêm",
    sourceVerified: "Đã xác minh",
    regulatedChip: "⚠ Nhạy cảm",
    riskyAlertTitle: "{{n}} thông tin rủi ro",
    riskyAlertDetails: "Xem chi tiết →",
    riskySheetTitle: "Claim cần kiểm tra giấy tờ",
    riskySheetSubtitle: "Nghị định 181/2013/NĐ-CP · mỹ phẩm / TPCN",
    riskySheetBody: "iSale phát hiện {{n}} claim trong sản phẩm này thuộc nhóm regulated. Nếu có giấy chứng nhận, đánh dấu bên dưới — iSale sẽ giảm cảnh báo trong script.",
    riskySheetAckLabel: "Tôi có giấy tờ",
    riskySheetCtaSave: "Lưu xác nhận",
    riskySheetCtaLater: "Để sau",
    riskySheetInfoTitle: "📚 Hiểu thêm",
    riskySheetInfoLine1: "Claim regulated vẫn được render — iSale thêm disclaimer trong caption",
    riskySheetInfoLine2: "Nếu chưa có giấy tờ, nên xoá để tránh rủi ro pháp lý",
    saveShortcutHint: "(⌘S)",
    editSheetTitle: "Sửa fact",
    createSheetTitle: "Thêm fact",
    sheetValueLabel: "Giá trị",
    sheetCharCount: "{{n}} / 200",
    sheetCancel: "Huỷ",
    sheetSave: "Lưu",
    sheetDelete: "🗑 Xoá fact này",
    sheetDeleteConfirmTitle: "Xoá fact này?",
    sheetSourceInfo: "Khi sửa, nguồn sẽ chuyển thành 'bạn nhập tay' — hãy xác nhận lại để dùng cho kịch bản",
    addedToast: "Đã thêm fact",
    deletedToast: "Đã xoá fact",
    saveFailedToast: "Không lưu được · {{err}}",
    deleteFailedToast: "Không xoá được · {{err}}",
    emptyStateTitle: "Chưa có fact nào",
    emptyStateBody:
      'Quay lại detail và bấm "Phân tích AI" để iSale tự đọc ảnh + đề xuất facts. Hoặc tự thêm fact bên dưới.',
    inputPlaceholder: "VD: SPF50+, không nhờn dính, ≤ 60 ký tự",
    riskyAccessibilityHint:
      "Mở sheet liệt kê các claim regulated với tuỳ chọn đánh dấu có giấy chứng nhận",
    /** FL-5 / GA-M-05: a11y label for the risky-claims banner (replaces hardcoded VN). */
    riskyAccessibilityLabel:
      "{{n}} claim rủi ro, double-tap để xem chi tiết và xác nhận có giấy tờ",
    saveChangesCta: "Lưu thay đổi",
    addCta: "Thêm fact",
    editSourceHint:
      "Khi sửa, nguồn sẽ chuyển thành 'bạn nhập tay' — hãy xác nhận lại để dùng cho việc tạo kịch bản.",
    createSourceHint: "Thông tin này được lưu với nguồn 'bạn nhập tay'. Hãy xác nhận nếu bạn chắc chắn.",
    fieldLabel: "field: {{field}}",
    addFailed: "Không thêm được · {{error}}",
  },
  factsWeb: {
    // GA-W-07 hardcode sweep (2026-06-05)
    emptyGroup: "— chưa có fact —",
    title: "Xác nhận facts",
    breadcrumb: "Xác nhận facts",
    /** B5 (marketing audit 2026-05-25) — replace obligation framing
     *  with seller-benefit framing. Names the consequence Linh actually
     *  fears (Shopee account suspension > vague "AI hallucination"). */
    introBody:
      "Anh/chị check ✓ những điểm AI tìm đúng — script sẽ chỉ dùng những fact này. Khách hỏi 'thật không?' anh/chị trả lời được, không sợ bị Shopee phạt vì claim sai.",
    progressDone: "{{n}}/{{total}} đã xác nhận",
    verifiedPercent: "{{p}}% · cần thêm {{n}} fact verified để mở khoá",
    riskyWarningCount: "{{n}} thông tin rủi ro",
    riskyWarningDetails: "Xem chi tiết",
    ctaCreateScript: "Tạo clip →",
    ctaCreateScriptNeeded: "Cần xác nhận thêm {{n}}",
    emptyTitle: "Sản phẩm chưa phân tích",
    emptyBody: "iSale chưa đọc ảnh sản phẩm này — quay lại trang chi tiết và bấm \"Phân tích AI\" để em tự rút facts. Quá trình mất ~25s.",
    emptyCtaPrimary: "🧠 Phân tích AI ngay",
    emptyCtaBack: "← Quay lại",
    drawerEditTitle: "Sửa fact",
    drawerCreateTitle: "Thêm fact",
    drawerSave: "Lưu",
    drawerSaveShortcut: "(⌘S)",
    drawerCancel: "Huỷ",
    drawerValueLabel: "Giá trị",
    drawerValueMaxHint: "Tối đa 200 ký tự",
    drawerCharCount: "{{n}} / 200",
    drawerDelete: "Xoá fact này",
    deleteConfirmTitle: "Xoá fact này?",
    deleteConfirmAction: "Xoá",
    deleteConfirmCancel: "Huỷ",
    addFactCta: "+ Thêm {{field}}",
    riskyModalTitle: "Claim cần kiểm tra giấy tờ",
    riskyModalSubtitle: "Quy định quảng cáo Việt Nam · mỹ phẩm / TPCN",
    riskyModalBody: "iSale phát hiện claim trong sản phẩm này thuộc nhóm regulated theo Nghị định 181/2013/NĐ-CP. Nếu anh có giấy chứng nhận, đánh dấu bên dưới — iSale sẽ giảm cảnh báo trong script.",
    riskyModalAckLabel: "Tôi có giấy tờ",
    riskyModalGroupLabel: "Group: {{group}}",
    riskyModalCtaSave: "Lưu xác nhận",
    riskyModalCtaLater: "Để sau",
    riskyModalInfoTitle: "📚 Hiểu thêm",
    sourceLabel: "{{source}} · confidence {{c}}%",
    factVerifiedLabel: "Đã xác nhận · {{c}}%",
    factPendingLabel: "Chờ xác nhận · {{c}}%",
    factRiskyLabel: "⚠ Regulated · cần kiểm tra giấy phép",
    helpTopic: "facts-web",
    loadingLabel: "Đang tải facts...",
    loadError: "Không tải được facts — kiểm tra mạng",
    retryAction: "Thử lại",
    saveFailedToast: "Không lưu được",
    deleteFailedToast: "Không xoá được",
    createScriptFailed: "Không tạo được script · thử lại",
    productNotReady: "Sản phẩm chưa sẵn sàng — chờ phân tích AI xong rồi thử lại",
  },
  render: {
    title: "Render",
    chooseMode: "Chọn chế độ render",
    chooseModeTitle: "Preview hay final?",
    fastPreview: "Fast preview",
    fastPreviewNote: "~60-90 giây · watermark iSale · 720p · không tốn quota",
    finalRender: "Final render",
    finalRenderNote: "~3 phút · 1080×1920 · watermark theo plan · tốn 1 video",
    pipelineLabel: "Quy trình xử lý AI",
    pipelineHint: "Bạn có thể làm việc khác — chúng tôi sẽ thông báo khi xong.",
    stageScript: "Viết kịch bản + hình ảnh",
    stageTts: "Lồng tiếng",
    stageCompose: "Ghép video",
    stageExport: "Xuất MP4 9:16",
    stageRunning: "đang chạy",
    longLoadingTitle: "Mẹo cho lần sau",
    longLoadingTip1: "Dùng ít hơn 5 ảnh input",
    longLoadingTip2: "Tắt AI hero scene nếu không cần",
    longLoadingTip3: "Render giờ thấp điểm (sau 23h)",
    errorTitle: "Có lỗi xảy ra",
    errorEmpathy: "Không phải lỗi của bạn — chúng tôi sẽ thử lại miễn phí, không tính quota.",
    errorRecommend: "KHUYẾN NGHỊ",
    errorBackHome: "Để sau · Về Trang chủ",
    // REM-016 parity fix — keys render.tsx calls that were missing from the
    // mobile `render` namespace (rendered as literal key-paths in the success/
    // cancelled/detail states). Mirrored from renderWeb.
    initJob: "Đang chuẩn bị…",
    pollingHint:
      "iSale đang xử lý — bạn có thể tắt app, sẽ push thông báo khi xong.",
    etaRemaining: "~{{seconds}}s nữa",
    tipCloseApp: "💡 Mẹo: Bạn có thể đóng app, chúng tôi sẽ thông báo khi xong.",
    stepLabel: "Bước: {{step}}",
    stepFallback: "khởi tạo",
    doneTitle: "Dựng xong",
    doneBody: "Bấm Tiếp để xuất file MP4 + ảnh bìa.",
    cancelledTitle: "Đã huỷ",
    detailsTitle: "Chi tiết kỹ thuật",
    detailsToggleShow: "Xem chi tiết kỹ thuật",
    detailsToggleHide: "Ẩn chi tiết",
    rowId: "Mã lần render",
    rowType: "Loại",
    rowStatus: "Trạng thái",
    rowProgress: "Tiến độ",
    rowStep: "Bước",
    rowWatermark: "Watermark",
    rowCdnUrl: "CDN URL",
    watermarkYes: "Có",
    watermarkNo: "Không",
    fallbackDash: "—",
    cancelCta: "Huỷ render",
    successToast: "Render xong · clip đã sẵn sàng 🎉",
    previewQueueFailed: "Không queue được preview · {{error}}",
    finalQueueFailed: "Không queue được final · {{error}}",
    // CF-H2: render reached before the clip is set up (no scene plan yet)
    preflightNoScenePlanTitle: "Chưa thiết lập clip",
    preflightNoScenePlanBody:
      "Chọn template, giọng đọc & nhạc trước khi render.",
    preflightSetupCta: "Thiết lập clip",
    scenePlanRedirect: "Chưa thiết lập clip — mở Editor để thiết lập trước.",
    // CF-Nit: cancel toasts (localized; no false "quota refunded" claim)
    cancelledToast: "Đã huỷ render",
    cancelFailToast: "Không huỷ được · {{error}}",
    // CF-H4: client render timeout (taking longer than the worker ETA)
    stuckTitle: "Lâu hơn dự kiến",
    stuckBody: "Render đang lâu bất thường. Thử tải lại, hoặc huỷ rồi dựng lại.",
    stuckRefreshCta: "Tải lại",
    openPreviewUrl: "Mở preview URL",
    ctaNextExport: "Tiếp → Export",
    bugReportAck: "Báo lỗi đã ghi nhận · Team iSale sẽ phản hồi trong 24h",
    errorDetailLabel: "CHI TIẾT LỖI",
    errorSessionLine: "Session: {{session}} · {{time}} · đã ghi log cho team",
    fallbackTryOther: "Thử provider khác",
    fallbackEta: "{{provider}} · ~{{eta}} giây",
    fallbackRetryLabel: "Thử lại {{label}}",
    fallbackRetryMeta: "~50 giây · miễn phí",
    bugReportTitle: "Báo lỗi cho team iSale",
    bugReportMeta: "Phản hồi trong 24h",
  },
  modals: {
    upgradeTitle: "Đã hết quota tháng này",
    upgradeBody: "Nâng cấp lên Starter để tạo thêm 30 clip/tháng.",
    upgradeCta: "Nâng cấp · Xem gói",
    upgradeLater: "Đợi tháng sau",
    costCapTitle: "Đã đạt hạn mức chi phí AI",
    costCapBody:
      "Bạn đã dùng hết ngân sách AI tháng này. Nâng cấp để tiếp tục tạo nội dung.",
    costCapCta: "Nâng cấp · Xem gói",
    costCapLater: "Để sau",
    logoutTitle: "Đăng xuất?",
    logoutBody: "Bạn cần đăng nhập lại để tạo clip.",
    deleteAccountTitle: "Xoá tài khoản vĩnh viễn",
    deleteAccountBody: "Mọi sản phẩm, clip, audit log sẽ bị xoá sau 30 ngày.",
    deleteAccountConfirmPhrase: "Tôi muốn xoá vĩnh viễn",
    cancelRenderTitle: "Huỷ render?",
    cancelRenderCta: "Huỷ render · không tốn quota",
    cancelRenderWait: "Tiếp tục đợi",
    shareTitle: "Chia sẻ clip",
    shareSubtitle: "Mở native share để chia sẻ clip, hoặc copy URL",
    shareTikTokSubtitle: "Mở app TikTok với MP4 + caption",
    shareFacebookLabel: "Facebook",
    shareFacebookSubtitle: "Share lên Reels / Story",
    shareInstagramLabel: "Instagram",
    shareInstagramSubtitle: "Share lên Reels",
    shareYoutubeLabel: "YouTube Shorts",
    shareYoutubeSubtitle: "Share lên Shorts dọc 9:16",
    shareShopeeLabel: "Shopee Video",
    shareShopeeSubtitle: "Upload video sản phẩm vào shop",
    shareCopyUrlLabel: "Sao chép URL",
    shareSheetOpened: "Đã mở share sheet",
    shareSheetFailed: "Không mở được share sheet",
    shareSuccess: "Đã chia sẻ clip",
    shareFailed: "Share thất bại",
    // Logout confirm modal
    logoutCta: "Đăng xuất",
    // Cancel render modal body
    cancelRenderBodyTitle: "Đang ở {{pct}}% — gần xong rồi!",
    cancelRenderBodyDesc:
      "Huỷ thì không tốn quota (chỉ trừ khi render thành công). Nếu render xong mà bạn không xem ngay cũng không tốn thêm gì.",
    // Delete product modal
    deleteProductTitle: "Xoá {{name}}?",
    deleteProductBody: "Ảnh, facts, và mọi clip liên quan sẽ bị xoá. Không thể hoàn tác.",
    deleteProductConfirm: "Xoá vĩnh viễn",
    // GA-X-04: Archive (soft-delete) product
    archiveProductTitle: "Lưu trữ {{name}}?",
    archiveProductBody:
      "Sản phẩm sẽ bị ẩn khỏi danh sách. Ảnh và facts vẫn được giữ nguyên — bạn có thể khôi phục từ Settings.",
    archiveProductConfirm: "Lưu trữ",
    archiveProductCancel: "Huỷ",
    archiveProductSuccess: "Đã lưu trữ sản phẩm",
    archiveProductCta: "Lưu trữ sản phẩm",
    // Voice preview modal
    voicePickerTitle: "Chọn giọng đọc",
    voicePickerSubtitle: "Nhấn ▶ để nghe thử trước khi chọn.",
    voicePickerUseCta: "Dùng giọng này",
    voicePickerPreviewA11y: "Nghe thử giọng {{name}}",
    voicePickerProBadge: "Pro",
    // Compliance warning modal
    complianceTitle: "Một số ý cần xem lại",
    complianceBody:
      "Những câu sau có thể vi phạm chính sách quảng cáo. Sửa hoặc bỏ trước khi tạo clip nhé.",
    complianceBackToFacts: "Quay lại sửa Fact Locker",
    complianceGuidance:
      'Hướng dẫn: dùng cụm khái quát ("hỗ trợ làm sạch") thay vì khẳng định công dụng tuyệt đối ("trị 100%").',
    deleteAccountConfirmCta: "Xoá tài khoản",
    deleteAccountUnlockCta: "Gõ đúng câu để mở khoá",
    deleteAccountTypePrompt: "Để xác nhận, gõ chính xác:",
    deleteAccountTypePlaceholder: "Gõ câu xác nhận...",
    deleteAccountRecoverHint:
      "Trong 30 ngày sau khi xoá bạn vẫn có thể khôi phục bằng cách đăng nhập lại.",
    // Upgrade modal tier-line labels
    tierBadgeCurrent: "Đang dùng",
    tierBadgePopular: "Phổ biến",
    tierVideosPerMonth: "{{n}} video/th",
  },
  intl: {
    today: "Hôm nay",
    yesterday: "Hôm qua",
    earlier: "Trước đó",
  },
  tabs: {
    home: "Trang chủ",
    products: "Sản phẩm",
    clips: "Clips",
    account: "Tôi",
  },
  onboarding: {
    skip: "Bỏ qua",
    continue: "Tiếp tục",
    start: "Bắt đầu",
    step: "Bước {{n}}/{{total}}",
    valueTitle: "Clip bán hàng AI\ntrong 3 phút",
    valueBody: "Chụp 3-5 ảnh sản phẩm — iSale tự viết script, lồng giọng VN, ghép clip 9:16 sẵn sàng đăng TikTok, Reels, Shorts & Shopee Video.",
    painTitle: "CapCut 2-3 giờ?\nGiờ chỉ 3 phút.",
    painBody: "Bỏ qua learning curve. Không cần biết edit. AI lo phần kịch bản + lồng tiếng + ghép cảnh — bạn chỉ duyệt + đăng.",
    howTitle: "3 bước · 1 chạm",
    howBody: "Chụp ảnh → AI phân tích & viết script → Bạn chọn bản rồi nhận file MP4. Không có giai đoạn 'render qua đêm'.",
    trustTitle: "Minh bạch + an toàn",
    trustBody: "Mọi cuộc gọi AI đều có audit log. Compliance check tự động cho mỹ phẩm, TPCN, dược. Bạn nắm quyền xoá dữ liệu bất kỳ lúc nào.",
    notifTitle: "Cho phép thông báo?",
    notifBody: "Chúng tôi báo bạn khi clip render xong (~30 giây nữa nhé!). Không spam — chỉ báo việc liên quan đến account của bạn.",
    notifAllow: "Cho phép thông báo",
    notifLater: "Để sau",
  },
  notificationsInbox: {
    /** Screen title for the in-app notification inbox (TASK 2A/2B). */
    title: "Thông báo",
    markAllRead: "Đánh dấu tất cả đã đọc",
    empty: "Chưa có thông báo nào",
    /** Per-type badge labels — match backend `type` column values. */
    badgeRenderDone: "Đã render",
    badgeRenderFailed: "Render lỗi",
    badgeQuotaWarn: "Quota",
    badgePaymentFailed: "Thanh toán",
    /** Relative timestamp formatting. */
    justNow: "vừa xong",
    minutesAgo: "{{n}} phút trước",
    hoursAgo: "{{n}} giờ trước",
  },
  profileOnboarding: {
    /** Menu row entry in Settings → reopens this onboarding carousel. */
    menuRowTitle: "Xem lại hướng dẫn",
    menuRowNote: "5 bước cách iSale hoạt động",
    /** 5 cards mirroring CARDS array in app/(tabs)/profile/onboarding.tsx. */
    card1Title: "Chụp ảnh sản phẩm",
    card1Body: "1-5 ảnh, JPG/PNG/WebP. iSale tự moderation, AI đọc + đề xuất điểm bán.",
    card2Title: "Verify Fact Locker",
    card2Body: "Bạn check ✓ vào facts AI tìm ra. Cần ≥3 verified để unlock script gen — chống AI bịa.",
    card3Title: "AI viết 3 kịch bản",
    card3Body: "AI sinh hook + scene + CTA. Bạn pick 1, sửa nếu cần.",
    card4Title: "Editor + Render nhanh",
    card4Body: "Chọn template + giọng + nhạc. Render preview 60s, final ~3 phút.",
    card5Title: "Tải MP4 + chia sẻ clip",
    card5Body: "Export 1080×1920 + caption + hashtag. Đăng thẳng TikTok, Reels, Shorts & Shopee Video.",
    /** CTA labels — reuse common.next + common.skip + onboarding.start where possible
     * but expose dedicated keys so future tone changes don't ripple across screens. */
    startCta: "Bắt đầu",
    nextCta: "Tiếp →",
    skipCta: "Bỏ qua",
    // Web SettingsOnboarding (vertical stack, all 5 cards visible at once).
    // Mobile carousel doesn't need step labels (page indicator dots replace).
    stepLabel: "Bước {{n}}/5",
    doneCta: "Hiểu rồi · Đóng",
  },
  // Auth-expired modal — global UX shown khi access token + refresh token
  // đều dead, hiển thị friendly message + 5s countdown + redirect /sign-in.
  // Đè replace technical toast "Missing or malformed Authorization header"
  // mà user non-tech không hiểu. (Vincent feedback 2026-05-23 PM.)
  auth: {
    sessionExpiredTitle: "Phiên đăng nhập đã hết hạn",
    sessionExpiredSubtitle: "Vì lý do an toàn, bạn cần đăng nhập lại để tiếp tục.",
    sessionExpiredBody:
      "Để bảo vệ tài khoản, iSale yêu cầu bạn đăng nhập lại sau một thời gian không hoạt động. Dữ liệu đang nhập (nếu có) đã được lưu nháp ở máy bạn.",
    sessionExpiredCountdown: "Tự động chuyển sau {{seconds}} giây…",
    sessionExpiredLoginNowCta: "Đăng nhập ngay",
    sessionExpiredLaterCta: "Để sau",
  },
  consent: {
    loading: "Đang tải điều khoản AI…",
    loadFailedTitle: "Không tải được điều khoản",
    loadFailedBody: "Kiểm tra kết nối mạng rồi thử lại.",
    retryCta: "Thử lại",
    saveFailed: "Không lưu được điều khoản · {{error}}",
    tryAgainLater: "Hãy thử lại sau",
    heroTitle: "iSale dùng AI để\ntạo clip cho bạn",
    heroSubtitle: "Đây là điều chúng tôi làm với dữ liệu của bạn",
    sendsTitle: "Chúng tôi gửi tới",
    sendsOnly: "Chỉ gửi:",
    sendsOnlyValue: "ảnh sản phẩm + thông tin bạn nhập",
    neverTitle: "KHÔNG bao giờ gửi",
    neverValue: "Email · thanh toán · workspace khác · ảnh cá nhân",
    policyLink: "Đọc chi tiết minh bạch AI",
    versionFooter: "Phiên bản điều khoản · v{{version}} · audit log lưu {{days}} ngày",
    acceptCta: "Tôi đồng ý · Tạo clip ngay",
    accepting: "Đang lưu…",
    // §6 B1 — reprompt modal strings, fired when backend returns 403
    // CONSENT_REQUIRED indicating the consent policy version changed.
    repromptTitle: "Điều khoản AI có cập nhật",
    repromptSubtitle: "Phiên bản {{version}} — cần bạn đồng ý lại để tiếp tục dùng tính năng AI.",
    repromptAcceptCta: "Đồng ý & tiếp tục",
    repromptViewCta: "Xem chi tiết",
    repromptWhatsNewTitle: "Có gì mới?",
    repromptWhatsNewBody:
      "Bổ sung quy định lưu giọng đọc · cập nhật danh mục dữ liệu gửi đi OpenAI · clip render được lưu tối đa 90 ngày (trước là 60).",
  },
  helpTooltip: {
    // 30-second tours surfaced by tapping the (?) glyph on a screen.
    // Topics: home / editor / facts / render. Each has 1 title + 3 steps.
    // Audit P2-02 (2026-05-26) — content moved out of HelpTooltipModal.
    acknowledgeCta: "Đã hiểu",
    reopenHint: "Mở lại tour này bất cứ lúc nào từ Cài đặt → Hướng dẫn.",
    homeTitle: "Trang chủ · 30 giây làm quen",
    homeStep1: "Nhấn nút lớn ở giữa để tạo clip mới.",
    homeStep2: "Hộp quota hiển thị số video còn lại tháng này.",
    homeStep3: "Mục 'Clip vừa tạo' là nơi xem lại video đã render.",
    editorTitle: "Editor · 30 giây",
    editorStep1: "Mỗi cảnh trong dòng thời gian = 1 đoạn ngắn của clip.",
    editorStep2: "Nhấn vào ảnh / chữ để sửa hoặc đổi vị trí.",
    editorStep3: "Nút 'Đổi giọng' mở picker giọng đọc miễn phí.",
    factsTitle: "Fact Locker · 30 giây",
    factsStep1: "AI gợi ý các điểm bán chính từ ảnh + tên sản phẩm.",
    factsStep2:
      "Bỏ chọn fact bạn không đồng ý — AI sẽ không dùng nó để viết script.",
    factsStep3: "Fact bị flag (màu vàng) cần được sửa hoặc xoá trước khi tiếp.",
    renderTitle: "Đang render · 30 giây",
    renderStep1: "Render mất 1–3 phút tuỳ độ dài + chất lượng.",
    renderStep2: "Bạn có thể đóng app, iSale sẽ thông báo khi xong.",
    renderStep3: "Nếu render > 5 phút, bạn có thể huỷ và thử lại miễn phí.",
  },
  emptyStates: {
    // Per-context empty states (audit P2-01). Each EmptyState component
    // (EmptyProducts / EmptyClips / EmptySearch / EmptyNotifications /
    // EmptyAILog) reads title + body + cta from here so a future content
    // tweak touches one place across vi/en/zh.
    productsTitle: "Chưa có sản phẩm nào",
    productsBody:
      "Thêm sản phẩm đầu tiên — chụp vài ảnh là iSale tự gợi ý fact bán hàng.",
    productsCta: "Thêm sản phẩm",
    clipsTitle: "Chưa có clip nào",
    clipsBody:
      "Sau khi render xong, các clip của bạn sẽ hiện ở đây để xem lại và chia sẻ.",
    clipsCta: "Tạo clip đầu tiên",
    searchInitialTitle: "Bắt đầu tìm kiếm",
    searchInitialBody:
      "Gõ tên sản phẩm, fact, hoặc clip để tìm nhanh trong workspace.",
    searchNoResultsTitle: 'Không có kết quả cho "{{query}}"',
    searchNoResultsBody: "Thử từ khoá khác hoặc bỏ filter để mở rộng kết quả.",
    notificationsTitle: "Chưa có thông báo",
    notificationsBody:
      "iSale sẽ báo bạn khi clip render xong hoặc có mẹo bán hàng mới.",
    notificationsCta: "Về trang chủ",
    aiLogTitle: "Chưa có hoạt động AI",
    aiLogBody:
      "Khi iSale gọi AI giúp bạn (đoán fact, viết script, sinh giọng), nhật ký sẽ hiện ở đây.",
    aiLogCta: "Tạo clip đầu tiên",
  },
  emptyHome: {
    greeting: "Chào bạn mới,",
    name: "bạn 👋",
    title: "Chưa có clip nào",
    subtitle: "Chụp 1 ảnh sản phẩm — 3 phút sau bạn có clip bán hàng.",
    step1: "Chụp ảnh sản phẩm",
    step1Note: "3–5 ảnh từ nhiều góc",
    step2: "Xác nhận info AI đoán",
    step2Note: "~30 giây",
    step3: "Nhận clip · đăng mọi nền tảng",
    step3Note: "~2 phút",
    cta: "+ Tạo clip đầu tiên",
    watchVideo: "Hoặc xem demo 60s ↗",
  },
  products: {
    // GA-M-05 hardcode sweep (2026-06-05)
    cameraPermissionError: "Quyền camera · {{error}}",
    libraryPermissionError: "Quyền thư viện · {{error}}",
    uploadError: "Lỗi khi upload · {{error}}",
    retryLaterHint: "Hãy thử lại sau",
    analyzeNeedImage: "Cần ít nhất 1 ảnh đã pass moderation để phân tích AI.",
    analyzeDone: "Phân tích xong · {{created}} fact mới ({{total}} total) · {{flags}}",
    analyzeFailed: "Phân tích thất bại · {{error}}",
    retryHint: "Hãy thử lại",
    title: "Sản phẩm",
    heroTitle: "Kho ảnh sản phẩm",
    heroBody: "Mỗi sản phẩm có ảnh + facts + đã verify → tạo clip bất kỳ lúc nào.",
    emptyTitle: "Chưa có sản phẩm nào",
    emptyBody: "Thêm sản phẩm để AI nhớ tên, giá, mô tả — dùng lại nhiều lần.",
    emptyCta: "+ Thêm sản phẩm",
    emptyCaptureCta: "Hoặc chụp 1 ảnh ngay",
    searchPlaceholder: "Tìm sản phẩm…",
    searchA11y: "Tìm sản phẩm",
    statusReady: "Sẵn sàng",
    statusDraft: "Nháp",
    gridSubtitle: "{{count}} sản phẩm · {{ready}} sẵn sàng tạo clip",
    gridAddCta: "Thêm sản phẩm",
    noImage: "Chưa có ảnh",
    searchEmpty: "Không tìm thấy sản phẩm phù hợp",
    detailTitle: "Sản phẩm",
    detailAnalyzeTitle: "Phân tích bằng AI",
    detailAnalyzeBody: "AI đọc ảnh, đề xuất điểm bán + đối tượng + claims. Bạn verify rồi mới dùng để tạo script (Fact Locker).",
    detailAnalyzeCta: "Phân tích ngay",
    /** GA-M-13: moved from productsWeb namespace — used on both mobile + web. */
    detailCreateClipCta: "Tạo clip từ sản phẩm này",
    detailVerifyFactsCta: "Xác nhận facts",
    detailAnalyzeBusy: "Đang phân tích…",
    detailAnalyzeCost: "Miễn phí (đã lưu)",
    detailAssetsLabel: "Ảnh",
    detailAssetsEmpty: "Chưa có ảnh nào.",
    detailEditTitle: "Sửa tên & danh mục",
    detailEditBody: "Chỉnh tên và danh mục sản phẩm",
    newTitle: "Sản phẩm mới",
    newStep1Kicker: "Bước 1 / 3",
    newStep1Title: "Thông tin sản phẩm",
    newStep1Body: 'Đặt tên ngắn gọn (ví dụ "Kem chống nắng SPF50"). Danh mục giúp AI biết quy tắc nào áp dụng.',
    newNameLabel: "Tên sản phẩm",
    newNamePlaceholder: "VD: Kem chống nắng SPF50",
    newCategoryLabel: "Danh mục",
    newStep2Kicker: "Bước 2 / 3",
    newStep2Title: "Ảnh sản phẩm",
    newStep2Body: "Chụp hoặc chọn 1-{{max}} ảnh. JPG/PNG/WebP, mỗi ảnh ≤ 5MB. Ảnh sạch + đủ sáng giúp AI đọc chính xác hơn.",
    newFramingTipTitle: "Đặt sản phẩm chính giữa khung",
    newFramingTipBody: "3–5 ảnh từ nhiều góc · nền sạch · ánh sáng đủ",
    newCameraCta: "Chụp",
    newLibraryCta: "Từ thư viện",
    newAssetsCount: "{{n}}/{{max}} ảnh",
    newAssetsEmpty: "Chưa có ảnh nào. Dùng nút bên trên để thêm.",
    newSubmitCta: "Tạo sản phẩm + upload {{n}} ảnh",
    newSubmitBusy: "Đang tạo sản phẩm…",
    newDoneTitle: "Tạo sản phẩm xong",
    newDoneApproved: "Đã duyệt",
    newDoneRejected: "Bị từ chối",
    newDoneFailed: "Lỗi upload",
    newDoneRejectedHint: "{{n}} ảnh bị moderation từ chối. Mở chi tiết để thay ảnh hoặc xoá.",
    newDoneDetail: "Xem chi tiết + phân tích AI",
    newDoneBack: "Về danh sách",
    // Category labels — used by products/new.tsx + products/[id]/edit.tsx
    // (deduped via t() per audit P2-05). Keys mirror backend value
    // strings on `products.category` so a code change there forces an
    // i18n key add as well.
    catCosmetic: "Mỹ phẩm",
    catSupplement: "TPCN",
    catFashion: "Thời trang",
    catFood: "Thực phẩm",
    catElectronics: "Đồ điện tử",
    catHome: "Đồ gia dụng",
    catOther: "Khác",
    // Upload phase labels (Hi-Fi 11 — user-facing rather than internal phase names)
    uploadInitializing: "Đang khởi tạo…",
    uploadHashing: "Đang chuẩn bị ảnh {{index}}/{{n}}",
    uploadConnecting: "Đang kết nối server…",
    uploadUploading: "Upload ảnh {{index}}/{{n}} · {{pct}}%",
    uploadConfirming: "AI đang phân tích {{n}} ảnh… · ~{{etaS}}s nữa",
    uploadFailed: "Ảnh {{index}} lỗi: {{error}}",
    uploadProcessing: "Đang xử lý ảnh…",
    // Validation + success toasts
    validateNameLen: "Tên cần 2-200 ký tự",
    editSaveSuccess: "Đã lưu thay đổi",
    editDeleteSuccess: "Đã xoá sản phẩm",
    detailFallbackName: "sản phẩm này",
    editTitle: "Sửa sản phẩm",
    editDirty: "Chưa lưu",
    editSaveCta: "Lưu thay đổi",
    editSaveBusy: "Đang lưu…",
    editSaveNoChange: "Không có thay đổi",
    editDangerTitle: "Nguy hiểm",
    editDangerCard: "Xoá sản phẩm",
    editDangerBody: "Ảnh, facts, và clip liên quan sẽ bị archive (soft-delete 30 ngày). Bạn có thể khôi phục từ Settings.",
    editDangerCta: "Xoá sản phẩm này",
  },
  projects: {
    listTitle: "Clip",
    listHeroTitle: "Clip đã tạo",
    listHeroBody: "Tất cả clip bạn đã render — chia sẻ clip, tải lại MP4, hoặc render lại với template/voice khác.",
    listLoading: "Đang tải clip…",
    listEmptyTitle: "Chưa có clip nào",
    listEmptyBody: "Tạo clip đầu tiên — AI sẽ học phong cách bạn thích.",
    filterAll: "Tất cả",
    filterA11y: "Lọc clip",
    searchPlaceholder: "Tìm clip…",
    searchA11y: "Tìm clip",
    filterInProgress: "Đang làm",
    sectionInProgress: "Đang làm dở",
    sectionDone: "Hoàn tất",
    sectionArchived: "Lưu trữ",
    stepScript: "Kịch bản",
    stepRender: "Dựng",
    stepExport: "Xuất",
    continueCta: "Tiếp tục →",
    filterEmpty: "Không có clip ở trạng thái này",
    listEmptyCta: "+ Tạo clip",
    newTitle: "Clip mới",
    newKicker: "Bước cuối trước khi sinh script",
    newHero: "Đặt tên + chọn độ dài",
    newBody: "AI sẽ tự viết 3 phiên bản kịch bản phù hợp tone + thời lượng.",
    newNameLabel: "Tên clip",
    newNamePlaceholder: "VD: Kem chống nắng SPF50 demo dùng buổi sáng",
    newDurationLabel: "Thời lượng (giây)",
    newToneLabel: "Tone giọng kịch bản",
    newSubmitCta: "Tạo + sinh 3 kịch bản",
    newSubmitBusy: "Đang tạo + generate…",
    newCostHint: "AI sinh 3 phiên bản kịch bản · ~10 giây",
    // Tone labels — used by NewClipModal tone picker
    toneCasual: "Thân thiện",
    toneProfessional: "Chuyên nghiệp",
    tonePlayful: "Vui nhộn",
    toneTrustworthy: "Uy tín",
    validateNameLen: "Tên cần 2-200 ký tự",
    scriptTitle: "Kịch bản",
    scriptEmptyTitle: "Chưa có kịch bản",
    scriptEmptyBody: "AI sẽ tạo 3 phiên bản kịch bản từ Facts đã xác minh. Chọn phiên bản phù hợp nhất để tiếp tục.",
    scriptGenerateCta: "Tạo kịch bản với AI",
    scriptGenerating: "AI đang soạn kịch bản…",
    scriptGeneratingBody: "Quá trình này mất khoảng 10–20 giây. Đừng tắt app.",
    scriptGenerateFail: "Tạo kịch bản thất bại. Thử lại?",
    scriptLoading: "Đang tải kịch bản…",
    scriptComplianceWarn: "{{n}} cảnh báo compliance",
    scriptComplianceBody: 'Đọc kỹ các câu thoại được flag bên dưới; nếu chứa từ vi phạm hãy bấm "Tạo lại" + nêu feedback cụ thể.',
    scriptRegenCta: "Tạo lại với góp ý…",
    scriptRegenTitle: "Góp ý cho lần sinh mới",
    scriptRegenBody: 'VD: "energetic hơn", "ngắn lại 20s", "đừng dùng từ trắng da".',
    scriptRegenPlaceholder: "Để trống = sinh ngẫu nhiên lại",
    scriptRegenSubmit: "Tạo lại 3 bản",
    scriptRegenBusy: "Đang sinh lại kịch bản…",
    scriptSelectBusy: "Đang lưu…",
    scriptSelectActive: "Đã chọn ✓",
    scriptSelectCta: "Chọn bản này",
    scriptCtaPick: "Chọn 1 bản để tiếp",
    scriptCtaNext: "Tiếp → Editor + Render",
    scriptCaptionBlock: "Caption + hashtag (tự sinh)",
    scriptCaptionLoading: "Đang sinh caption…",
    scriptCaptionEmpty: "Caption sẽ xuất hiện sau khi chọn bản.",
    scriptSelectedBadge: "★ ĐANG CHỌN",
    scriptToneEnergetic: "Năng động",
    scriptToneRefined: "Tinh tế",
    scriptToneFun: "Vui tươi",
    scriptSelectFailed: "Không chọn được · {{error}}",
    scriptRegenSuccess: "Đã sinh 3 variant mới",
    scriptRegenFailed: "Không sinh lại được · {{error}}",
    scriptVariantMeta: "{{seconds}}s · {{chars}} chữ",
    scriptMoreScenes: "+ {{n}} cảnh nữa",
  },
  editor: {
    title: "Editor Lite",
    // Compact summary pill rendered in the screen header — replaces the
    // legacy USD cost badge (audit P2-11). Plurals are not handled (Vi has
    // no plural form change) so a single key is enough.
    scenePillSummary: "{{scenes}} cảnh · {{seconds}}s",
    tabTemplate: "Mẫu",
    tabVoice: "Giọng",
    tabMusic: "Nhạc",
    tabSubtitle: "Subtitle",
    templateBody: "Aspect 9:16 cho mọi nền tảng video ngắn",
    voiceCount: "{{n}} giọng tiếng Việt Neural",
    voiceWarn: "⚠ Đổi voice = re-TTS toàn bộ scene (POST mới).",
    musicCount: "{{n}} track license-cleared",
    musicNone: "(Không nhạc nền)",
    musicNoneSub: "Chỉ giọng đọc",
    subtitleBody: "Style chữ trên video (khuyến nghị: Noto Sans VN)",
    engineLabel: "Engine render",
    engineFast: "Fast Mode",
    engineFastNote: "Free · ~3s render · Ken Burns + TTS",
    engineAi: "AI Mode",
    engineAiNote: "AI hero scene · Phase 2 PRO",
    planSummary: "Phân cảnh hiện tại",
    planSummaryBody: "{{scenes}} scene · {{seconds}}s tổng · {{cacheHits}} scene cache-hit (free)",
    saveCta: "Dựng phân cảnh + render →",
    saveUpdateCta: "Lưu thay đổi + render →",
    saveBusy: "Đang lưu phân cảnh…",
    missingInfo: "Cần chọn template + giọng trước khi render.",
    subtitleStyleWhite: "Trắng cơ bản",
    subtitleStyleYellow: "Vàng nổi bật",
    voiceGenderFemale: "Nữ",
    voiceGenderMale: "Nam",
    voicePreviewA11y: "Mở trình nghe thử giọng",
    voicePreviewCta: "Nghe thử",
    voicePreviewPreparing: "Đang chuẩn bị nghe thử {{name}}…",
  },
  export: {
    title: "Export + Share",
    heroTitle: "Clip đã sẵn sàng 🎉",
    heroPreviewLabel: "9:16 preview",
    heroMeta: "MP4 · 9:16 · session {{session}}",
    readyKicker: "Sẵn sàng đăng",
    readyTitle: "Tải file + share",
    watermarkTitle: "Watermark iSale đang bật",
    watermarkBody: "Video gói miễn phí đều có watermark. Lên Pro để gỡ.",
    captionLabel: "Caption + hashtag",
    platformLabel: "Nền tảng đích",
    platformHint: "Hashtag tự điều chỉnh theo nền tảng — không tạo lại AI",
    copyForPrefix: "Sao chép để đăng",
    captionLoading: "Đang tạo caption + hashtag…",
    captionEmpty: "—",
    formatsLabel: "Định dạng file",
    formatMp4: "MP4",
    formatThumbnail: "Ảnh bìa",
    formatSrt: "SRT",
    formatAll: "Tất cả",
    existingLabel: "Đã xuất ({{n}})",
    existingEmpty: "Bấm 1 nút bên trên để xuất file.",
    shareCta: "Chia sẻ clip",
    shareHint: "Mở native share sheet · caption + hashtag + URL pre-filled",
    actionView: "Xem",
    actionEdit: "Sửa",
    actionRerender: "Render lại",
    actionExport: "Xuất",
  },
  aiActivity: {
    title: "Nhật ký AI",
    intro: "Mỗi lần AI xử lý cho bạn đều được ghi lại — minh bạch & bạn có quyền yêu cầu xoá.",
    filterAll: "Tất cả",
    loading: "Đang tải audit log…",
    emptyAll: "Chưa có audit log nào.",
    emptyProvider: "Chưa có cuộc gọi nào của {{provider}}.",
    sectionToday: "HÔM NAY",
    sectionYesterday: "HÔM QUA",
    sectionEarlier: "TRƯỚC ĐÓ",
    capAnalyzeImage: "Phân tích ảnh",
    capGenerateScript: "Sinh script",
    capGenerateCaption: "Caption + hashtag",
    capCheckCompliance: "Compliance check",
    capSynthesizeVoice: "Voice-over",
    capGenerateVideo: "Phối cảnh video",
    rowProviderLabel: "Hệ thống AI:",
    rowSessionLabel: "session",
    rowTokensUnit: "tokens",
    rowLatencyUnit: "ms",
    loadMore: "Xem thêm",
    loadingMore: "Đang tải thêm…",
    showingOf: "Hiển thị {{shown}} / {{total}} mục",
  },
  profileEdit: {
    title: "Sửa hồ sơ",
    dirty: "Chưa lưu",
    changeAvatar: "Đổi ảnh đại diện",
    avatarHint: "Sắp có — đổi ảnh đại diện ở bản cập nhật tới.",
    nameLabel: "Tên hiển thị",
    namePlaceholder: "Tên bạn dùng trong app",
    emailLabel: "Email (đọc-only)",
    emailHint: "Đổi email = đăng nhập với account khác.",
    localeLabel: "Ngôn ngữ",
    localeHint: "Lưu cũng đẩy lên Stripe checkout + audit log.",
    saveCta: "Lưu thay đổi",
    saveShort: "Lưu",
    saveBusy: "Đang lưu…",
    saveNoChange: "Không có thay đổi",
    loading: "Đang tải hồ sơ…",
    loadError: "Không tải được hồ sơ",
    saved: "Đã lưu hồ sơ",
  },
  themePicker: {
    title: "Giao diện",
    intro: 'Chọn chế độ giao diện. "Tự động" sẽ theo cài đặt hệ điều hành — sáng ban ngày, tối khi bạn bật dark mode trên iOS / Android.',
    lightLabel: "Chế độ sáng",
    lightDesc: "Nền trắng · chữ đen · cho ban ngày",
    darkLabel: "Chế độ tối",
    darkDesc: "Nền tối · chữ sáng · đỡ mỏi mắt buổi tối",
    systemLabel: "Tự động theo hệ thống",
    systemDesc: "Đổi giao diện theo OS · vi/en/zh đều OK",
    betaKicker: "BETA",
    betaBody: "Một số màn hình cũ chưa đổi màu theo giao diện. Chúng tôi đang hoàn thiện dần để toàn bộ ứng dụng đồng bộ.",
    toastSystem: "Theo hệ thống",
    toastDark: "Chế độ tối",
    toastLight: "Chế độ sáng",
  },
  languagePicker: {
    // GA-M-05 sweep (2026-06-05) — also de-jargons "Đổi local OK"
    syncFailed: "Đã đổi ngôn ngữ trên máy; chưa đồng bộ với server ({{error}})",
    title: "Ngôn ngữ",
    intro: "Chọn ngôn ngữ hiển thị. Tiếng Việt là mặc định. Tiếng Anh và Tiếng Trung đã được dịch đầy đủ.",
    parityLabel: "Glossary parity",
    parityBody: "Mọi key đều có sẵn 3 ngôn ngữ. Currency tự động chuyển VND ↔ USD ↔ CNY theo locale. Date format đi theo Intl API.",
    restartTitle: "Cần khởi động lại app",
    restartToZh: "Chuyển sang Tiếng Trung yêu cầu reload native modules.",
    restartFromZh: "Chuyển khỏi Tiếng Trung yêu cầu reload native modules.",
    restartCta: "Đổi & nhắc tôi đóng app",
    restartConfirmed: "Đã đổi ngôn ngữ · vui lòng đóng app rồi mở lại để hoàn tất",
    toastSwitched: "Đã chuyển sang {{name}}",
    nativeViet: "Xin chào!",
    nativeEn: "Hello!",
    nativeZh: "你好!",
    requiresRestartSuffix: "· cần restart app",
  },
  dashboard: {
    kicker: "Tổng quan",
    newClipCta: "Tạo clip mới",
    quotaLabel: "THÁNG NÀY",
    quotaRemaining: "Plan {{plan}} · còn {{n}} clip",
    upgradePro: "Nâng cấp Pro →",
    backendDown: "Chưa kết nối được máy chủ iSale. Kiểm tra mạng rồi tải lại.",
    tipsKicker: "MẸO NHANH",
    tipsBody:
      "Mỗi sản phẩm nên có 3-5 ảnh chất lượng tốt + bạn verify ≥ 3 facts trước khi sinh script. AI viết script chính xác hơn khi facts được xác minh.",
    emptyProducts: "Chưa có sản phẩm — sang Sản phẩm để tạo.",
    emptyClips: "Chưa có clip — sang Clip để generate.",
    openLink: "Mở",
    seeAll: "Xem tất cả →",
  },
  notifications: {
    bellLabel: "Thông báo",
    drawerTitle: "Thông báo · {{count}} mới",
    readAll: "Đọc hết",
    loading: "Đang tải…",
    loadError: "Không tải được thông báo",
    emptyTitle: "Hộp thư trống",
    emptyBody: "Khi clip render xong hoặc có cập nhật, em sẽ báo anh ở đây.",
    relativeNow: "vừa xong",
    relativeMin: "{{n}} phút",
    relativeHour: "{{n}} giờ",
    relativeDay: "{{n}} ngày",
  },
  upload: {
    /** Screen-reader-only labels for the per-asset moderation badge in
     *  Product Detail. Visual badge gets a friendly title via
     *  friendlyRejectReason(); these strings power the assistive tech
     *  fallback so the user hears "đã duyệt" instead of the raw key. */
    phase_approved: "Ảnh đã duyệt",
    phase_pending: "Ảnh đang chờ duyệt",
    phase_rejected: "Ảnh bị từ chối",
    idleTitle: "Kéo thả ảnh sản phẩm vào đây",
    idleSubline: "Hoặc bấm để chọn từ máy · JPG / PNG / WebP · tối đa 10 ảnh",
    dragHere: "Thả ảnh ở đây",
    rejectTitle: "Không hỗ trợ file này",
    rejectSubline: "File '{{file}}' bị từ chối · {{reason}}",
    reasonType: "định dạng không hỗ trợ",
    reasonSize: "vượt giới hạn 5MB",
    hintClick: "Bấm để chọn file",
    hintPaste: "Cmd+V để paste",
    hintFolder: "Kéo thư mục",
    or: "hoặc",
    pickFromDevice: "Chọn từ máy",
    pickFolder: "Tải thư mục",
    pasteCta: "Dán từ clipboard",
    tipKicker: "Mẹo",
    tipBody: "Chụp 3-5 ảnh từ nhiều góc giúp AI phân tích chính xác hơn.",
    tooManyFiles: "Chọn {{selected}} ảnh nhưng chỉ còn {{room}} slot · các ảnh thừa bị bỏ qua",
    // Generic — raw {{error}} interpolation removed for same reason as
    // urlsFail (non-tech users confused by "Request was blocked" /
    // "Failed to fetch" raw browser errors). Vincent 2026-05-23 PM.
    prepareFail: "Lỗi chuẩn bị ảnh. Vui lòng thử lại.",
    preparing: "Đang chuẩn bị ảnh…",
    headerCount: "{{count}}/10 ảnh · {{size}}",
    headerMax: "{{count}}/10 ảnh · {{size}} · đã đạt giới hạn",
    headerUploading: "Đang upload {{done}}/{{total}} · {{pct}}% tổng",
    clearAll: "Xoá tất cả",
    continue: "Tiếp tục",
    cancelAll: "Huỷ tất cả",
    addMore: "Thêm ảnh",
    maxTip: "Lưu tip: 5-7 ảnh thường đủ cho AI phân tích — không cần 10",
    errorTitle: "{{count}} ảnh không upload được",
    errorGeneric: "Upload thất bại",
    retryAll: "Thử lại tất cả",
    removeFile: "Xoá ảnh",
    rejectedOpen: "Xem chi tiết kiểm duyệt",
    rejectedChip: "Vi phạm chính sách",
    moderating: "Đang kiểm duyệt",
    confirmTitle: "Xác nhận upload",
    confirmStart: "Bắt đầu upload",
    confirmSummary: "{{count}} ảnh · {{size}} · sẵn sàng gửi lên",
    confirmAiHint: "Sau khi upload, AI sẽ phân tích — bạn có thể chỉnh sửa trước khi tạo script.",
    kbdHint: "Bấm Esc để huỷ · Enter để xác nhận",
    cancelTitle: "Huỷ upload?",
    cancelKeepGoing: "Tiếp tục upload",
    cancelDoIt: "Huỷ upload",
    cancelBody: "{{done}}/{{total}} ảnh đã xong. Huỷ sẽ xoá tất cả ảnh đã upload và quay về dropzone.",
    pasteTitle: "Dán ảnh từ clipboard",
    pasteAdd: "Thêm vào danh sách",
    // Generic failure message — no raw {{error}} interpolation. Auth-
    // expiry (401) now bypassed in MultiFileDropzone catch + handled by
    // global AuthExpiredModal. Network/5xx hits show this generic copy;
    // raw error message goes to console.error only. Vincent 2026-05-23.
    urlsFail: "Không thể chuẩn bị upload. Vui lòng kiểm tra mạng và thử lại.",
    completeTitle: "Upload xong!",
    completeBody: "{{count}} ảnh đã sẵn sàng. AI có thể bắt đầu phân tích.",
    completeRejectedNote: "{{count}} ảnh bị kiểm duyệt từ chối, không tính vào batch.",
    completeEta: "Phân tích mất ~15 giây · không tính vào quota",
    // Renamed from "Bắt đầu phân tích AI" (2026-05-23) — the button used
    // to navigate to /projects/{productId} which 404'd because that
    // route expects a project id, not a product id. Now just closes
    // the modal + refetches the product page. User starts AI analysis
    // by clicking "Tạo clip từ sản phẩm này" on the product detail
    // page once they have ≥3 approved photos.
    completeAnalyze: "Xong, xem sản phẩm",
    completeAddMore: "Tải thêm ảnh",
    // All-rejected state — tất cả ảnh bị moderation từ chối, không có ảnh nào sẵn sàng.
    // Hiển thị warning thay vì celebration (UX bug Vincent flagged 2026-05-22 PM).
    rejectedAllTitle: "Tất cả ảnh bị từ chối",
    rejectedAllBody: "Cần ít nhất 1 ảnh qua kiểm duyệt để AI phân tích sản phẩm.",
    rejectedAllReasonLabel: "Lý do từ AI kiểm duyệt",
    rejectedAllHint: "Thử ảnh sản phẩm rõ nét hơn · tránh ảnh có người mặt hoặc nội dung không phù hợp.",
    rejectedAllRetry: "Tải ảnh khác",
    moderationDrawerTitle: "Ảnh không qua kiểm duyệt",
    moderationReasonLabel: "Lý do",
    moderationDefaultReason: "AI phát hiện nội dung không phù hợp",
    moderationPolicyLink: "Xem chính sách kiểm duyệt",
    moderationDelete: "Xoá ảnh",
    // Friendly reject-reason mapping — translates raw backend errors into
    // user-readable Vietnamese. Used by `lib/moderation-display.ts`
    // mapper. Raw reason still preserved in the drawer's expandable
    // "Chi tiết kỹ thuật" section. Vincent feedback 2026-05-23 PM.
    rejectReasons: {
      // System: Workers AI license gate (Meta Llama error 5016)
      systemLicense_title: "Ảnh chưa kiểm duyệt được",
      systemLicense_body: "Mô hình kiểm duyệt AI chưa được kích hoạt đầy đủ trên môi trường này. Đây là vấn đề kỹ thuật của hệ thống, không liên quan tới ảnh bạn upload.",
      systemLicense_hint: "Vui lòng thử lại sau hoặc liên hệ admin nếu tình trạng kéo dài.",
      // System: AI runtime / provider error (4xx/5xx) — recoverable
      systemAi_title: "Hệ thống kiểm duyệt tạm gián đoạn",
      systemAi_body: "Dịch vụ kiểm duyệt AI tạm thời không phản hồi đúng cách. Ảnh của bạn không có vấn đề gì.",
      systemAi_hint: "Vui lòng thử upload lại sau vài phút.",
      // System: R2 fetch failed (object missing / network glitch)
      systemR2_title: "Lỗi đọc ảnh từ server",
      systemR2_body: "Không đọc lại được ảnh từ kho lưu trữ. Thường do upload chưa hoàn tất hoặc lỗi mạng.",
      systemR2_hint: "Vui lòng thử upload lại.",
      // System: AI returned malformed JSON
      unparseable_title: "Kết quả kiểm duyệt không hợp lệ",
      unparseable_body: "AI trả về kết quả không đúng định dạng. Đây là lỗi kỹ thuật của hệ thống.",
      unparseable_hint: "Vui lòng thử lại.",
      // Post-hoc: stale upload abandoned (sweeper-set reason)
      abandoned_title: "Upload bị bỏ dở",
      abandoned_body: "Upload trước đó bị dừng giữa chừng (đóng app/trình duyệt sớm).",
      abandoned_hint: "Vui lòng upload lại ảnh.",
      // Size: file too large per backend cap
      size_title: "Ảnh quá lớn",
      size_body: "Ảnh vượt quá kích thước cho phép (tối đa 10MB).",
      size_hint: "Hãy nén ảnh hoặc chọn ảnh có dung lượng nhỏ hơn.",
      // Content: real moderation reject — pass through AI's explanation
      content_title: "Nội dung không phù hợp",
      content_body: "{{detail}}",
      content_hint: "Vui lòng dùng ảnh sản phẩm khác. Tránh ảnh có nội dung nhạy cảm.",
      // Unknown / empty
      unknown_title: "Bị từ chối",
      unknown_body: "AI không cung cấp lý do cụ thể.",
      // Expandable technical detail (drawer footer)
      technicalDetailLabel: "Chi tiết kỹ thuật",
      technicalDetailHint: "Dùng cho dev / support escalation",
      // Count badge for grouped reasons in all-rejected sheet
      groupCountSuffix: "{{count}} ảnh",
    },
  },
  productsWeb: {
    createCta: "Tạo sản phẩm",
    uploadFor: "Upload ảnh sản phẩm",
    createTitle: "Tạo sản phẩm mới",
    createSubmit: "Tạo",
    createBusy: "Đang tạo sản phẩm…",
    createFailPrefix: "Không tạo được:",
    uploadHint:
      'Tải ảnh lên trong app mobile — bản web chưa hỗ trợ kéo-thả.',
    tableName: "Tên",
    tableCategory: "Danh mục",
    tableStatus: "Trạng thái",
    tableUpdated: "Cập nhật",
    // GA-W-12: was `emptyHintMobile` — renamed (no Mobile suffix; used on web route only)
    emptyHint:
      'Bấm "Tạo sản phẩm" để bắt đầu hoặc dùng app mobile để upload ảnh + facts.',
    // Card list (replaces table 2026-05-21)
    cardCategory: "Danh mục",
    cardUpdated: "Cập nhật {{date}}",
    cardNoCover: "Chưa có ảnh",
    cardOpen: "Mở",
    // Detail page (W-Pr-01)
    detailBackToList: "Quay lại Sản phẩm",
    detailBreadcrumb: "Sản phẩm",
    detailAssetsTitle: "Ảnh sản phẩm",
    detailAssetsEmpty: "Chưa có ảnh — upload bên dưới để bắt đầu.",
    detailUploadTitle: "Tải ảnh sản phẩm",
    detailUploadMore: "Tải thêm ảnh",
    detailCreateClipCta: "Tạo clip từ sản phẩm này",
    detailVerifyFactsCta: "Xác nhận facts",
    detailNotReady: "Cần 3 ảnh được duyệt để tạo clip.",
    detailLoading: "Đang tải sản phẩm…",
    detailLoadError: "Không tải được sản phẩm",
    detailRetry: "Thử lại",
    detailAuthErrorTitle: "Phiên đăng nhập đã hết hạn",
    detailAuthErrorCta: "Đăng nhập lại",
    detailJoinedAt: "Tạo {{date}}",
    detailAssetCountReady: "{{n}} ảnh sẵn sàng",
    detailAssetCountPending: "{{n}} đang duyệt",
    detailAssetCountRejected: "{{n}} bị từ chối",
    // EP-7: template-context banner on product list
    templateBannerUsing: "Mẫu đã chọn:",
    templateBannerPickHint: "Chọn sản phẩm bên dưới để bắt đầu tạo clip với mẫu này.",
    templateBannerClear: "Huỷ mẫu",
  },
  projectsWeb: {
    // GA-W-12: was `mobileHint` — renamed (describes web features, not a mobile hint)
    featureHint:
      "Edit kịch bản, đổi voice/music/template, render preview hoặc final, và xuất file MP4 / SRT / caption.",
    // GA-W-12: was `emptyHintMobile` — renamed
    emptyHint: "Chưa có clip nào. Bấm \"Tạo clip\" để bắt đầu từ một sản phẩm đã sẵn sàng.",
    cardOpen: "Mở",
    cardCreated: "Tạo {{date}}",
    // New clip modal
    newCta: "Tạo clip",
    newModalTitle: "Tạo clip mới",
    newProductLabel: "Chọn sản phẩm",
    newProductPlaceholder: "— Chọn sản phẩm đã ready —",
    newNoReadyProducts: "Chưa có sản phẩm trạng thái Ready. Hãy tải ảnh + verify facts trước.",
    newSubmitting: "Đang tạo clip…",
    newSubmitFail: "Không tạo được clip · {{error}}",
    newSubmitHint: "Chọn sản phẩm và nhập tên để tạo",
    goToProducts: "Đi tới Sản phẩm",
  },
  billingWeb: {
    currentLabel: "Đang dùng",
    tierName: "Gói {{name}}",
    resetCost: "Reset {{date}} · cost ${{used}} / ${{cap}}",
    starterFeature1: "30 video/tháng",
    starterFeature2: "Bỏ watermark",
    starterFeature3: "5 giọng VN premium",
    proFeature1: "150 video / tháng",
    proFeature2: "AI hero scene (Kling)",
    proFeature3: "Brand kit + template tuỳ chỉnh",
    enterpriseFeature1: "Unlimited video",
    enterpriseFeature2: "API access · SLA",
    enterpriseFeature3: "Team workspace",
    enterpriseTagline: "Cho team & shop lớn",
    enterprisePrice: "Liên hệ",
    perMonth: "/th",
    perYear: "/năm",
    checkoutFail: "Không tạo được checkout:",
    freeFeature1: "3 video / tháng",
    freeFeature2: "Render Fast Mode (không Kling)",
    freeFeature3: "Watermark iSale",
    lifetimeFeature1: "30 video / tháng",
    lifetimeFeature2: "AI Mode (Kling i2v): 3 video/tháng",
    lifetimeFeature3: "Ưu tiên hỗ trợ + beta features",
    lifetimeTagline: "Trả 1 lần, dùng mãi",
    lifetimePeriod: "một lần",
    lifetimePrice: "2.990.000₫",
    emailIntentTitle: "Đăng ký Lifetime sớm",
    emailIntentSubtitle: "Giới hạn 200 người · Chúng tôi sẽ liên hệ khi mở thanh toán.",
    emailIntentEmailPlaceholder: "email@cua-ban.com",
    emailIntentEmailLabel: "Địa chỉ email",
    emailIntentCta: "Đăng ký",
    emailIntentSuccess: "Đã ghi nhận! Chúng tôi sẽ liên hệ sớm.",
    emailIntentError: "Không gửi được — thử lại.",
    emailIntentInvalid: "Email không hợp lệ.",
  },
  aiActivityWeb: {
    /** B10 (marketing audit 2026-05-25) — replace bureaucratic title
     *  with question that engages the user + answers Linh's real fear
     *  ("Will OpenAI see my private product photos?"). */
    heroTitle: "Em đã làm những gì cho anh/chị?",
    introFull:
      "Mỗi lần AI giúp anh/chị (đọc ảnh, viết script, lồng tiếng) — em ghi lại ở đây để anh/chị check. Không ai khác xem được clip hay sản phẩm của anh/chị ngoài AI providers liệt kê dưới. Log tự xoá sau 90 ngày.",
    pillSummary: "{{n}} lượt dùng AI",
    tableWhen: "Khi",
    tableCapability: "Tính năng AI",
    tableProviderModel: "Hệ thống AI (mô hình)",
    tableCost: "Chi phí",
    tableLatency: "Tốc độ xử lý (ms)",
    tableStatus: "Trạng thái",
  },
  projectDetailWeb: {
    loadingProject: "Đang tải clip…",
    notFoundTitle: "Không tìm thấy clip",
    backToClips: "Về danh sách Clip",
    backAllClips: "Tất cả Clip",
    pillLabel: "Clip",
    productLabel: "Sản phẩm:",
    productNone: "—",
    versionPill: "v{{version}}",
    emptyScriptTitle: "Chưa có kịch bản",
    emptyScriptBody:
      "AI sẽ tạo 3 phiên bản kịch bản từ những Facts đã xác minh. Chọn phiên bản phù hợp nhất để tiếp tục.",
    generateScriptCta: "Tạo kịch bản với AI",
    generatingScript: "AI đang soạn kịch bản…",
    generatingScriptBody: "Quá trình này mất khoảng 10–20 giây. Đừng đóng trang.",
    generateScriptFail: "Tạo kịch bản thất bại. Thử lại?",
    complianceWarnCount: "{{n}} cảnh báo tuân thủ",
    complianceWarnBody:
      'Đọc kỹ câu thoại bị đánh dấu bên dưới; nếu chứa từ vi phạm hãy bấm "Tạo lại" + nêu góp ý cụ thể.',
    providerFallback: "Không rõ",
    modelFallback: "—",
    selectFailAlert: "Không chọn được bản.",
    regenFailAlert: "Không sinh lại được.",
    variantLabel: "Bản {{n}}",
    variantMeta: "{{scenes}} cảnh · {{seconds}}s",
    variantMoreScenes: "+ {{n}} cảnh nữa",
    variantCtaLabel: "Lời kêu gọi",
    captionTitle: "Caption + hashtag (tự sinh)",
    captionLoading: "Đang sinh caption…",
    captionEmpty: "Caption sẽ xuất hiện sau khi chọn bản.",
    regenCta: "Tạo lại với góp ý",
    regenBusy: "Đang sinh lại…",
    ctaPick: "Chọn 1 bản để tiếp",
    ctaNext: "Tiếp → Editor Lite",
    variantSelected: "Đã chọn ✓",
    variantSelect: "Chọn bản này",
    variantSaveBusy: "Đang lưu…",
    modalTitle: "Góp ý cho lần sinh mới",
    modalBody: 'VD: "energetic hơn", "ngắn lại 20s", "đừng dùng từ trắng da".',
    modalPlaceholder: "Để trống = sinh ngẫu nhiên lại",
    modalSubmit: "Tạo lại 3 bản",
    modalClose: "Đóng",
  },
  editorWeb: {
    backToScript: "Về kịch bản",
    heroTitle: "Chọn voice · nhạc · template",
    estCostBadge: "~${{cost}} est.",
    tabSubtitleEngine: "Phụ đề & chế độ",
    sectionTemplateTitle: "Tỷ lệ 9:16 cho mọi nền tảng video ngắn",
    sectionVoiceTitle: "{{n}} giọng tiếng Việt Neural",
    sectionVoiceWarn:
      "⚠ Đổi voice = re-TTS toàn bộ scene (POST mới, không phải PATCH).",
    sectionMusicTitle: "{{n}} track license-cleared",
    sectionMusicNoneTitle: "(Không nhạc nền)",
    sectionMusicNoneSub: "Chỉ giọng đọc",
    sectionSubtitleTitle: "Style chữ trên video",
    sectionEngineTitle: "Engine render",
    subtitleDefaultWhite: "Trắng cơ bản",
    subtitleBoldYellow: "Vàng nổi bật",
    engineFastTitle: "Fast Mode",
    engineFastBody:
      "Free · ~3s render · Ken Burns + TTS · không tốn AI quota",
    engineAiTitle: "AI Mode",
    engineAiBody: "PRO · AI hero scene cao cấp · Phase 2 (sắp ra mắt)",
    templateSubtitle: "{{aspect}} · chuyển cảnh: {{transition}}",
    voiceSubtitleFemale: "♀ · {{locale}}",
    voiceSubtitleMale: "♂ · {{locale}}",
    musicSubtitle: "{{mood}} · {{bpm}} BPM · {{seconds}}s",
    musicLicense: "License: {{license}}",
    planTitle: "Phân cảnh hiện tại",
    planBody:
      "{{scenes}} scene · {{seconds}}s tổng · {{cacheHits}} scene cache-hit (free)",
    saveBusy: "Đang lưu phân cảnh…",
    saveUpdate: "Lưu thay đổi + render →",
    saveCreate: "Dựng phân cảnh + render →",
    missingTemplateOrVoice: "Cần chọn template + giọng.",
    needVariantFirst: "Hãy chọn 1 kịch bản trước khi thiết lập clip.",
    saveFailPrefix: "Không lưu được.",
    sectionLoading: "Đang tải tuỳ chọn…",
  },
  renderWeb: {
    backToEditor: "Về Editor Lite",
    heroTitle: "Preview hay final?",
    heroBody:
      "Render preview free + nhanh nhưng có watermark + chất lượng thấp. Final cần quota + lâu hơn nhưng 1080×1920 sạch (paid tier).",
    fastPreviewTitle: "Xem trước nhanh",
    fastPreviewBody: "~60-90 giây · watermark iSale · 720p · không tốn quota",
    fastPreviewBadge: "FREE",
    fastPreviewCta: "Tạo bản xem trước",
    finalRenderTitle: "Bản hoàn chỉnh",
    finalRenderBody: "~3 phút · 1080×1920 · watermark theo plan · tốn 1 video",
    finalRenderBadge: "QUOTA",
    finalRenderCta: "Dựng bản hoàn chỉnh",
    queueingBusy: "Đang chuẩn bị…",
    queuePreviewFail: "Không khởi tạo được clip xem trước",
    queueFinalFail: "Không khởi tạo được clip cuối",
    cancelFail: "Không cancel được",
    initJob: "Đang chuẩn bị…",
    pollingHint:
      "iSale đang xử lý — bạn có thể tắt app, sẽ push thông báo khi xong.",
    stepLabel: "Bước: {{step}}",
    stepFallback: "khởi tạo",
    doneTitle: "Dựng xong",
    doneBody: "Bấm Tiếp để xuất file MP4 + ảnh bìa.",
    doneBodyEmbedded: "Tải file & đăng bài ở ngay bên dưới 👇",
    errorTitle: "Render lỗi",
    errorUnknown: "Không rõ lỗi.",
    cancelledTitle: "Đã huỷ",
    detailsTitle: "Chi tiết kỹ thuật",
    detailsToggleShow: "Xem chi tiết kỹ thuật",
    detailsToggleHide: "Ẩn chi tiết",
    rowId: "Mã lần render",
    rowType: "Loại",
    rowStatus: "Trạng thái",
    rowProgress: "Tiến độ",
    rowStep: "Bước",
    rowWatermark: "Watermark",
    rowCdnUrl: "CDN URL",
    watermarkYes: "Có",
    watermarkNo: "Không",
    fallbackDash: "—",
    cancelCta: "Huỷ render",
    retryCta: "↻ Thử lại",
    openPreviewUrl: "Mở bản xem trước",
    copyUrl: "Sao chép đường dẫn",
    nextExport: "Tiếp → Export",
    // GA-W-09: preflight guard — no script yet
    preflightNoScriptTitle: "Chưa có kịch bản",
    preflightNoScriptBody:
      "Clip cần có kịch bản trước khi render. Quay lại Editor để tạo kịch bản.",
    preflightGoToEditorCta: "Về Editor Lite",
    // CF-H2: render reached before the clip is set up (no scene plan yet)
    preflightNoScenePlanTitle: "Chưa thiết lập clip",
    preflightNoScenePlanBody:
      "Chọn template, giọng đọc & nhạc trong Editor trước khi render.",
    scenePlanRedirect: "Chưa thiết lập clip — mở Editor để thiết lập trước.",
    // CF-H4: client render timeout (taking longer than the worker ETA)
    stuckTitle: "Lâu hơn dự kiến",
    stuckBody: "Render đang lâu bất thường. Thử tải lại, hoặc huỷ rồi dựng lại.",
    stuckRefreshCta: "Tải lại",
  },
  exportWeb: {
    backToRender: "Về Render",
    pillLabel: "Export + Share",
    heroTitle: "Tải file + share",
    heroBody:
      "Sẵn sàng đăng TikTok, Reels, Shorts & Shopee Video. Web copy URL + caption; mobile có native share sheet.",
    watermarkTitle: "Watermark iSale đang bật",
    watermarkBody: "Video gói miễn phí đều có watermark. Lên Pro để gỡ.",
    captionTitle: "Caption + hashtag",
    platformLabel: "Nền tảng đích",
    platformHint: "Hashtag tự điều chỉnh theo nền tảng — không tạo lại AI",
    copyForPrefix: "Sao chép để đăng",
    captionLoading: "Đang tạo caption + hashtag…",
    captionFallback: "—",
    formatsTitle: "Định dạng file",
    /** P3.2 — referral / organic-growth attribution toggle. */
    attributionToggleTitle: 'Đính kèm "Made with iSale" ✨',
    attributionToggleNote:
      "Thêm 1 dòng ghi nguồn vào caption khi sao chép. Giúp người khác biết tới iSale từ clip của bạn.",
    attributionLine: "Tạo bằng iSale ✨ isale.app",
    /** P3.1 — First-clip celebration moment (Strategy §7 retention). */
    celebrationTitle: "🎉 Clip đầu tiên xong rồi!",
    celebrationSubtitle: "Mất chưa tới 3 phút — đúng promise của iSale.",
    /** B8 + B7 (marketing audit 2026-05-25) — first clip = ACTIVATE
     *  moment per Strategy §4.2. Push share aggressively, defer the
     *  upgrade nudge to clip #3 (HABIT threshold). Use "anh/chị"
     *  register instead of generic "bạn" to align brand voice. */
    celebrationBody:
      "Đăng ngay khi clip còn 'nóng' — algorithm thích clip mới. Copy caption + URL bên dưới, đăng thẳng lên TikTok, Reels, Shorts & Shopee Video — em đã chuẩn bị sẵn caption + hashtag rồi.",
    celebrationCopyCta: "Copy caption + URL",
    celebrationUpgradeCta: "Bỏ watermark — Lên Pro",
    celebrationDismissCta: "Để sau",
    noRenderHint: "Chưa có render thành công cho clip này.",
    goToRender: "Sang Render →",
    formatMp4: "MP4",
    formatThumbnail: "Ảnh bìa",
    formatSrt: "SRT",
    formatAll: "Tất cả",
    existingTitle: "Đã xuất ({{n}})",
    existingEmpty: "Bấm 1 nút bên trên để xuất file.",
    rowSizeExpires: "{{kb}} KB · expires {{date}}",
    copyUrlAria: "Sao chép đường dẫn",
    openFileAria: "Mở file",
    openFileTitle: "Mở / tải",
    copySharePrefix: "Copy caption + URL để đăng clip",
    shareHint: "Đường dẫn sẽ hết hạn sau 24 giờ",
    copySuccess: "Đã copy caption + hashtag + URL",
    copyNeedMp4: 'Chưa có MP4 — bấm "Tất cả" hoặc "MP4" trước.',
    exportFailPrefix: "Không xuất được.",
    /** FL-5: mobile export hardcode sweep. */
    exportNeedRenderFirst:
      "Cần render thành công trước khi xuất file. Quay về Render trước.",
    exportSuccessCount: "Đã xuất {{n}} file",
  },
  settingsWeb: {
    deleteContactSubtitle:
      "Email tới privacy@isale.app · team sẽ xử lý trong 30 ngày theo GDPR.",
    deleteUnderstood: "Đã hiểu",
    deleteContactToast: "Liên hệ privacy@isale.app để xoá dữ liệu",
    /** V027 — real DELETE /me wired (no longer "email support"). */
    deleteRealSubtitle:
      "Tài khoản sẽ bị vô hiệu hoá ngay lập tức. Bạn có 30 ngày để khôi phục trước khi mọi dữ liệu bị xoá vĩnh viễn.",
    deleteRealBody:
      "Sau khi xác nhận: tất cả phiên đăng nhập của bạn sẽ bị huỷ. Sản phẩm và clip vẫn được giữ trong 30 ngày để khôi phục nếu cần. Sau 30 ngày, tất cả sản phẩm, clip, ảnh và lịch sử của bạn sẽ bị xoá vĩnh viễn theo GDPR. Bạn không cần gửi email cho bộ phận hỗ trợ.",
    deleteConfirmCta: "Xoá tài khoản của tôi",
    deleteBusy: "Đang xoá…",
    deleteSuccessToast: "Tài khoản đã được đánh dấu xoá. Bạn có 30 ngày để khôi phục.",
    deleteFailPrefix: "Không xoá được tài khoản",
  },
  themeWeb: {
    back: "Quay lại Cài đặt",
    intro:
      'Chọn chế độ giao diện. "Tự động" sẽ theo cài đặt hệ điều hành.',
    systemDesc:
      "Đổi giao diện theo OS · prefers-color-scheme media query",
    betaKicker: "BETA",
    betaBody:
      "Một số trang cũ chưa đổi màu theo giao diện. Chúng tôi đang hoàn thiện dần để toàn bộ giao diện đồng bộ.",
  },
  languageWeb: {
    back: "Quay lại Cài đặt",
    intro:
      "Tiếng Việt là mặc định. EN + ZH glossary đã dịch sẵn — sẽ phủ full screen sau khi i18n migration sweep xong.",
    requiresReloadSuffix: "· cần reload tab",
    reloadTitle: "Cần reload tab",
    reloadToZh:
      "Chuyển sang Tiếng Trung cần reload để load font CJK tối ưu.",
    reloadFromZh:
      "Chuyển khỏi Tiếng Trung cần reload để giải phóng font CJK.",
    reloadCta: "Đổi & reload",
    skipReloadCta: "Đổi không reload",
    reloadToast: "Đã đổi ngôn ngữ · vui lòng reload tab để hoàn tất.",
    localOnlyWarning:
      "Đổi local OK; chưa đồng bộ với server ({{error}})",
  },
  oauthCallback: {
    working: "Đang hoàn tất đăng nhập…",
    redirecting: "Đang chuyển hướng…",
    errorMissing: "Thiếu thông tin trả về từ Google",
    errorWithCode: "Google từ chối · {{code}}",
    errorExchange: "Không xác thực được phiên đăng nhập · {{error}}",
  },
  profileEditWeb: {
    back: "Quay lại Cài đặt",
    saveFail: "Không lưu được · {{error}}",
    loadErrorFallback: "Kiểm tra kết nối backend.",
    avatarUploading: "Đang upload…",
    avatarUploaded: "Đã cập nhật ảnh đại diện",
    avatarUploadFail: "Upload ảnh thất bại · {{error}}",
    avatarTooLarge: "Ảnh quá lớn — tối đa 5MB",
    // D4 W-P-01 new fields
    breadcrumbRoot: "Cài đặt",
    breadcrumbCurrent: "Hồ sơ",
    h1: "Hồ sơ cá nhân",
    joinedAt: "Tham gia {{date}}",
    phoneLabel: "Số điện thoại",
    phonePlaceholder: "+84 ...",
    tiktokLabel: "Link shop / mạng xã hội",
    tiktokPlaceholder: "@your-handle",
    bioLabel: "Bio Shop",
    bioPlaceholder: "Mô tả ngắn về cửa hàng của bạn (tối đa 200 ký tự).",
    avatarChipLabel: "Đổi ảnh đại diện",
  },
  settingsSubnav: {
    kicker: "Cài đặt",
    profile: "Hồ sơ",
    appearance: "Giao diện",
    language: "Ngôn ngữ",
    notifications: "Thông báo",
    privacy: "Quyền & dữ liệu",
    quota: "Quota & gói",
    aiActivity: "Hoạt động AI",
  },
  planAside: {
    kicker: "Gói hiện tại",
    planFree: "Free",
    planPro: "Pro",
    usageLine: "{{used}}/{{cap}} video · {{remaining}} còn",
    upgradeCta: "Nâng cấp Pro",
    invoicesTitle: "3 hoá đơn gần đây",
    invoiceLine: "{{date}} · ${{amount}} {{status}}",
    invoiceOk: "✓",
    invoiceFail: "✗",
  },
  // D4 W-P-03 Notification preferences (web)
  notifPrefs: {
    breadcrumbCurrent: "Thông báo",
    h1: "Thông báo",
    groupRender: "Render & xuất",
    groupQuota: "Quota & gói",
    groupEmail: "Email",
    renderDone: "Clip render xong",
    renderFailed: "Render thất bại",
    renderReady: "Clip sẵn sàng tải",
    quotaWarn: "Còn 20% quota",
    quotaFull: "Hết quota tháng",
    quotaPromo: "Khuyến mãi nâng cấp",
    emailWeekly: "Tóm tắt hằng tuần",
    previewKicker: "Xem trước",
    previewPushHint: "📱 Push trên điện thoại",
    previewToastHint: "💬 In-app toast",
    previewAppName: "iSale",
    previewPushTitle: "✓ Clip \"Serum HA\" đã sẵn sàng",
    previewPushBody: "Tap để xem · 30s · 9:16",
    previewToastBody: "Render xong · 30s",
    previewFootnote: "Bản preview cập nhật khi bạn bật/tắt từng mục.",
    previewDisabled: "TẮT",
    saveBusy: "Đang lưu…",
    savedLocal: "Đã lưu thông báo (cục bộ)",
  },
  nav: {
    dashboard: "Trang chủ",
    products: "Sản phẩm",
    clips: "Clip",
    billing: "Gói cước",
    aiActivity: "Nhật ký AI",
    settings: "Cài đặt",
    workspace: "Cửa hàng",
    signOut: "Đăng xuất",
    signOutSuccess: "Đã đăng xuất",
  },
  routes: {
    dashboard: "Trang chủ",
    products: "Sản phẩm",
    clips: "Clip",
    clipNew: "Tạo clip",
    projectScript: "Kịch bản",
    /** Used as the parent-title for back-link on /projects/:id/(editor|render|export). */
    projectDetailParent: "Chi tiết clip",
    editor: "Editor Lite",
    render: "Render",
    export: "Xuất + Chia sẻ",
    billing: "Gói cước",
    aiActivity: "Nhật ký AI",
    settings: "Cài đặt",
    settingsTheme: "Giao diện",
    settingsLanguage: "Ngôn ngữ",
    settingsNotifications: "Thông báo",
    settingsOnboarding: "Xem lại hướng dẫn",
    profileEdit: "Chỉnh sửa hồ sơ",
    productDetail: "Chi tiết sản phẩm",
    productFacts: "Xác nhận facts",
    clipTemplates: "Mẫu clip",
    clipTemplateDetail: "Chi tiết mẫu",
  },
  // MP10 — Transactional email templates (5 templates × 3 locales)
  emails: {
    shared: {
      footer: "Email tự động từ iSale · không cần trả lời",
      helpLink: "Hỗ trợ",
      privacyLink: "Quyền riêng tư",
      tosLink: "Điều khoản",
    },
    welcome: {
      subject: "Chào mừng đến với iSale 🎉",
      preheader: "3 phút có clip bán hàng đầu tiên",
      heading: "Chào {{name}} 👋",
      body1:
        "Tạo clip bán hàng từ ảnh sản phẩm bằng AI — không cần biết edit. Chụp 3-5 ảnh, iSale lo phần kịch bản, lồng giọng VN, ghép clip 9:16 sẵn sàng đăng TikTok, Reels, Shorts & Shopee Video.",
      cta: "Bắt đầu tạo clip",
      freeTierNote: "Bạn đang dùng gói Free — 5 clip miễn phí mỗi tháng.",
    },
    renderDone: {
      subject: "Clip {{name}} đã sẵn sàng 🎬",
      preheader: "Mở xem ngay — clip vừa render xong",
      heading: "Clip {{name}} đã xong",
      body1:
        "Mở xem trước khi đăng — bạn có thể tải MP4 trực tiếp hoặc chia sẻ qua TikTok, Reels, Shorts & Shopee Video từ link bên dưới.",
      meta: "{{duration}} · {{size}}",
      cta: "Xem clip",
      ctaSecondary: "Hoặc mở trong app:",
    },
    renderFailed: {
      subject: "Render {{name}} chưa thành công",
      preheader: "Không tính quota — bạn có thể thử lại miễn phí",
      heading: "Có chút trục trặc khi render",
      body1:
        "Không phải lỗi của bạn — chúng tôi sẽ thử lại miễn phí, không tính quota. Bạn có thể nhấn nút bên dưới để render lại ngay.",
      errorCodeLabel: "Mã lỗi: {{code}}",
      fallbackSuggestion: "Gợi ý: thử lại với provider {{provider}} (chậm hơn 1 chút nhưng ổn định hơn).",
      noQuotaCharge: "Không tốn quota cho lần render thất bại này.",
      cta: "Thử lại miễn phí",
    },
    quotaWarn: {
      subjectWarn: "Còn ít quota render tháng này",
      subjectFull: "Đã hết quota render tháng này",
      preheader: "Bạn đã dùng {{used}}/{{cap}} clip",
      headingWarn: "Sắp hết quota tháng này",
      headingFull: "Quota tháng này đã hết",
      body1:
        "Bạn đã dùng {{used}}/{{cap}} clip. Quota sẽ reset trong {{days}} ngày, hoặc nâng cấp lên Pro để tiếp tục ngay.",
      cta: "Nâng cấp lên Pro",
      tip: "Pro: 150 video/tháng + bỏ watermark + AI hero scene.",
    },
    paymentFailed: {
      subject: "Thanh toán chưa thành công",
      preheader: "Cập nhật thẻ để giữ quyền lợi Pro",
      heading: "Thanh toán không thành công",
      body1:
        "Stripe không trừ được tiền từ thẻ hiện tại. Account vẫn ở gói Pro cho đến cuối kỳ — bạn chỉ cần cập nhật thẻ trước thời điểm thử lại để tránh bị hạ về Free.",
      attemptCount: "Lần thử thứ {{n}}",
      nextRetryAt: "Stripe sẽ thử lại vào {{date}}",
      cta: "Cập nhật thẻ thanh toán",
      supportLink: "Gặp vấn đề? Liên hệ support@isale.app",
    },
  },
} as const;

export default vi;
export type TranslationBundle = typeof vi;
